/* WHITAKER PRODUCT PAGE - the redesign's behavior layer (ships with the theme).

   What this file does: adapts the native calculator's elements (rendered by
   snippets/wk-calculator.liquid in the Apippa-shaped markup it inherited) into
   the redesign (option cards, binding picker, dimension grid, unit switch, qty
   bar), splits the product title, builds the photo rail, and runs the scroll
   choreography (pull-back, holds, the state-3 sticky freeze). Nothing here
   computes a price: the amounts on the page come from wk-calculator.js, which
   mirrors the pricing service's rules; the add-on notes mirror its config for
   display only.

   Its partners, wired in layout/theme.liquid (product pages only):
     - snippets/wk-product-page.liquid  the window.__wk* globals (Liquid-only
       data: asset URLs, the collection-driven profile, the samples gate) -
       rendered before this script's tag
     - snippets/apippa-dimension-fields.liquid  the zero-select behavior on
       the four size inputs - independent, renders earlier in the head
     - the PRODUCT PAGE REDESIGN half of assets/custom.css ("section N of the
       page CSS" throughout refers to its numbered sections)

   HISTORY THAT SHAPES THIS FILE: it was written against Apippa's app, which
   injected #calculator late, rebuilt it on every change and dropped hidden
   fields out of its formula. Apippa went 2026-09-30 (docs/APIPPA-REMOVAL-PLAN.md);
   Phase 3 of that plan renders the final markup on the server and retires the
   adaptation layer here. Until then the guarded, re-runnable builders stay as
   they are: the selects and radios remain the source of truth (write .value,
   then fire input AND change or the price goes stale). */
(function () {
  'use strict';
  /* One guard so the file cannot run twice on one page. */
  if (window.__wkProductPage) return;
  window.__wkProductPage = true;

  /* ONE OWNER of "set a live Apippa control's value and make it reprice".
     Apippa recalculates on input AND change, and setting .value alone leaves
     the price stale, so every write goes value -> input -> change, in that
     order (op-01: this replaced two local write() helpers and four inline
     copies of the same three lines - the qty stepper, the qty cap, the
     inches clamp, setBinding). String() is idempotent on the strings the
     callers already pass. */
  function wkSet(el, value) {
    el.value = String(value);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* Has Apippa rendered its calculator? Everything waits on this: the app
     injects #calculator late and rebuilds it on every option change. */
  function realCalculator() {
    var c = document.getElementById('calculator');
    return c && c.querySelectorAll('.element').length > 0 ? c : null;
  }

  function start() {
    /* The real calculator arrives late - and the design must land on it in
       the SAME pre-paint moment. A 250ms poll plus setTimeout-scheduled
       re-applies meant Apippa's bare markup could paint first and the stubs,
       labels and bars push everything around a beat later: the fields visibly
       jumped down to make room for what arrived after them. Both paths are
       now synchronous inside mutation microtasks, so the first frame the
       calculator ever paints is the finished design. Convergent because every
       applyDesign step is a guarded no-op once its work exists: the pass its
       own mutations trigger finds nothing to do and the chain stops. */
    function initDesign() {
      if (window.__wkDesignInit || !realCalculator()) return;
      window.__wkDesignInit = true;
      applyDesign();
      /* Apippa REBUILDS #calculator when options change, which throws away
         everything added to it. Every applyDesign step is a no-op when its
         work is already there, so re-running on any change to the container
         puts back whatever was lost - synchronously, before the frame with
         the bare rebuild can paint. */
      var applying = false;
      var host = realCalculator().parentNode || document.body;
      new MutationObserver(function () {
        if (applying) return;
        applying = true;
        try { applyDesign(); } finally { applying = false; }
      }).observe(host, { childList: true, subtree: true });
    }

    /* The calculator is rendered with the page (Phase 3b-5, 2026-09-30): no
       observer and no poll wait for it; the design lands in this same pass.
       The re-run observer inside initDesign stays for now (3d revisits it). */
    initDesign();

    /* The title split does not need the calculator, and waiting for it meant
       the page painted "Antigua Pewter Indoor/ Outdoor" full-size and then
       reshaped seconds later when the material dropped to its own line -
       a visible jolt right above the loading skeleton. Done first. */
    splitTitle();
    /* ONE reveal instead of three flashes. The title area went fallback font,
       then webfont, then the split reshaped it - three paints of three
       different titles. The blocking CSS holds it invisible from first paint;
       it fades in once, split already done, when the webfonts are in (with a
       timer so a stalled font never strands it). */
    (function () {
      var t = document.querySelector('.product-single__title');
      if (!t) return;
      var done = false;
      function ready() { if (!done) { done = true; t.classList.add('wk-title-ready'); } }
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(ready, ready);
      window.setTimeout(ready, 1500);
    })();
    watchReveals();

    wrapCalcColumn();
    buildRail();
    moveRateToRail();
    scrollPullback();
    revealCartOnQuantity();
    stopGalleryDrag();
    clickToOpenState2();
    watchGuidedScroll();
    /* THE HOLD COMES OFF. snippets/wk-product-page.liquid set wk-pp-hold on
       <html> before first paint so the blocks reshaped above (tabs, the
       description, the share links) never painted in stock form. Everything
       that moves has moved by this line; whatever did NOT (no rail on the
       pad page, a product whose key points did not build) is simply shown
       where it stands, which is the truthful fallback. */
    document.documentElement.classList.remove('wk-pp-hold');
  }

  /* The element lookups run against Apippa's REAL calculator. (The design-era
     stand-in stubs for Shape and Protective Treatment are gone - the master
     calculator has owned both since 2026-08-25; what the admin still owes the
     redesign is tracked in APIPPA-CONSOLIDATION-PLAN.md.) */
  function elementFor(name) {
    /* The names are the theme's own (snippets/wk-calculator.liquid): "Width Feet",
       never Apippa's older "Width (Feet)" (that fallback went in the sweep of 2026-10-07). */
    return document.querySelector('#calculator .element[data-element="' + name + '"]');
  }

  /* The rug pad page: no info rail, no photo pin - the pair sits centred.
     The body class comes from theme.liquid via snippets/wk-is-rug.liquid. */
  function padMode() {
    return document.body.classList.contains('wk-pad-product');
  }

  /* THE OPTION CARDS are rendered by snippets/wk-calculator.liquid (Phase
     3b-2, 2026-09-30): label.wk-textopt inside .wk-textopts, titles, notes,
     the border photographs and the greyed card with its reason. What is left
     here is behaviour the markup cannot carry: the tap-to-show tip on a card
     that cannot be chosen (jute's Serged), and the Round note read from the
     price table in case the surcharge ever changes. Idempotent, like every
     applyDesign step. */
  function nativeRoundSurcharge() {
    try {
      var s = document.getElementById('wk-price-table');
      var t = s ? JSON.parse(s.textContent) : null;
      var n = t && parseFloat(t.round_surcharge_usd);
      return isFinite(n) && n > 0 ? n : 0;
    } catch (e) { return 0; }
  }
  function adaptRadios() {
    var calc = document.getElementById('calculator');
    if (!calc) return;
    [].forEach.call(calc.querySelectorAll('.wk-textopt--off'), function (wrap) {
      if (wrap.__wkTipWired) return;
      wrap.__wkTipWired = true;
      wrap.addEventListener('click', function (ev) {
        ev.preventDefault();
        wrap.classList.add('is-tipped');
        clearTimeout(wrap.__tipT);
        wrap.__tipT = setTimeout(function () { wrap.classList.remove('is-tipped'); }, 2400);
      });
    });
    var note = calc.querySelector('[data-wk-round-note]');
    if (note) {
      var rs = nativeRoundSurcharge();
      var want = rs > 0 ? '+$' + rs : '';
      if (note.textContent !== want) note.textContent = want;
    }
  }

  /* Rectangle or Round - now Apippa's own element, wired rather than injected.
     Round is priced as the square of its diameter plus $50; the calculator's
     conditional shows the element only for a Custom size. What this still owns
     is presentation: the data-wk-shape attribute the CSS folds the Length
     fields with (height-collapse, never display:none), and the Width label's
     crossfade into Diameter. */
  function addShape() {
    var el = elementFor('Choose Shape');
    if (!el) return;

    function apply() {
      var round = el.querySelector('input[value="Round"]').checked;
      /* The Length elements are NOT display-hidden here, and must never be.
         Apippa drops a hidden field from its own formula: measured 2026-08-24,
         the same 7x9 rug recalculated at $497 with Length visible and $59 with
         Length display:none. The CSS hides them with the same clip pattern the
         Quantity select already uses - out of sight, still in the formula. */
      var calc = document.getElementById('calculator');
      var changed = calc && calc.getAttribute('data-wk-shape') !== (round ? 'round' : 'rectangle');
      if (calc && changed) calc.__wkFieldsMoving = performance.now();
      /* WRITE ONLY ON A REAL CHANGE (2026-09-03, owner-reported bounce). apply()
         re-runs on every applyDesign pass; an unconditional setAttribute fired a
         data-wk-shape mutation every pass even with the same value, and that
         attribute is in the height observer's watch list, so each no-op write
         re-aimed the running height travel - measured 206 spurious writes in a
         few toggles. setAttribute must be gated on `changed`, like everything
         else in this file that writes to the DOM. */
      if (calc && changed) calc.setAttribute('data-wk-shape', round ? 'round' : 'rectangle');
      /* On a round rug the width fields ARE the diameter, so they say so. */
      var wf = elementFor('Width Feet');
      /* The inches-mode row's label is our own text, so it renames directly -
         the CSS ::after overlay it used to need is gone with section 28R. */
      var rowLab = document.querySelector('.wk-inches__row .wk-inches__label');
      if (rowLab && rowLab.firstChild && rowLab.firstChild.nodeType === 3) {
        var want = round ? 'Diameter' : 'Width';
        if (rowLab.firstChild.textContent !== want) rowLab.firstChild.textContent = want;
      }
      if (wf) {
        /* The painted word swaps Width <-> Diameter through a CSS content
           change, which cannot interpolate - so the label crossfades instead
           of blinking. Only on a real change: apply() also runs on re-adds. */
        var lab = wf.querySelector('label');
        if (changed && lab && lab.animate &&
            !(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches)) {
          lab.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 240, easing: 'ease' });
        }
      }
    }
    if (!el.__wkShapeWired) {
      el.__wkShapeWired = true;
      el.addEventListener('change', apply);
    }
    apply();
  }

  /* THE DIMENSION GRID, the inches rows and the unit switch are rendered by
     snippets/wk-calculator.liquid (Phase 3b-3, 2026-09-30); addUnitSwitch
     wires the rendered markup once. */

  /* FEET AND INCHES, OR PLAIN INCHES.

     The four fields stay the source of truth and keep being what the price
     reads; this only changes how a number gets into them. Typing 108 writes 9
     into feet and 0 into inches, because the inches fields cap at 11.5 and
     cannot hold 108 themselves. Every write dispatches input and change, which
     is what makes the calculator reprice. */
  function addUnitSwitch() {
    var calc = document.getElementById('calculator');
    var wf = elementFor('Width Feet');
    var bar = calc && calc.querySelector('.wk-units');
    var stub = calc && calc.querySelector('.wk-inches');
    if (!wf || !bar || !stub || bar.__wkWired) return;
    bar.__wkWired = true;

    var PAIRS = [['Width Feet', 'Width Inches'], ['Length Feet', 'Length Inches']];

    function field(n) {
      var e = elementFor(n);
      return e && e.querySelector('input.numInput-cpc');
    }
    function num(el) { var n = parseFloat(el && el.value); return isFinite(n) ? n : 0; }

    var boxes = [].map.call(stub.querySelectorAll('.wk-inches__row'), function (row, i) {
      var pair = PAIRS[i];
      var input = row.querySelector('.wk-inches__input');
      input.addEventListener('input', function () {
        var feet = field(pair[0]), inches = field(pair[1]);
        if (!feet || !inches) return;
        var total = Math.max(0, Math.round(num(input)));
        /* The ceiling is the page's maximum in whole feet (13 x 40): 13' 11"
           was never a legal size. A capped entry is reported, never quietly
           absorbed: the row's max line lights and the box flashes. */
        var cap = maxFeetFor(pair[0]) * 12;
        if (total > cap) {
          total = cap;
          input.value = String(cap);
          flashClamp(pair[0], input, '(Max ' + (i === 0 ? 'width' : 'length') + ' is ' +
                     maxFeetFor(pair[0]) + ' feet, set to ' + cap + ' inches)');
        }
        wkSet(feet, Math.floor(total / 12));
        wkSet(inches, total % 12);
      });
      return { row: row, input: input, pair: pair };
    });
    var btns = bar.querySelectorAll('.wk-units__btn');
    [].forEach.call(btns, function (b, i) {
      b.addEventListener('click', function () { setMode(i === 1); });
    });

    function setMode(inches) {
      [].forEach.call(btns, function (b, i) {
        var on = (i === 1) === inches;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      var want = inches ? 'inches' : 'ftin';
      if (calc.getAttribute('data-wk-units') !== want) {
        calc.__wkFieldsMoving = performance.now();
        calc.setAttribute('data-wk-units', want);
      }
      if (inches) {
        /* Carry the size across rather than making them retype it. */
        boxes.forEach(function (b) {
          b.input.value = String(num(field(b.pair[0])) * 12 + num(field(b.pair[1])));
        });
      }
    }
    setMode(false);
  }

  /* The area the customer has actually asked for, in square feet, read from
     whichever branch is live. Used only to price the add-ons in the design; the
     rug's own total is still Apippa's. */
  function currentArea() {
    var el = function (n) { return elementFor(n); };
    var custom = el('Width Feet') && el('Width Feet').style.display !== 'none';
    function val(n) {
      var e = el(n) && el(n).querySelector('input.numInput-cpc');
      var v = parseFloat(e && e.value);
      return isFinite(v) ? v : 0;
    }
    if (custom) {
      var w = val('Width Feet') + val('Width Inches') / 12;
      var round = document.querySelector('#calculator [data-element="Choose Shape"] input[value="Round"]');
      var l = (round && round.checked) ? w : (val('Length Feet') + val('Length Inches') / 12);
      return w * l;
    }
    var picked = document.querySelector('#calculator [data-element="Standard Stock Sizes"] input:checked');
    var area = parseFloat(picked && picked.getAttribute('data-val'));
    return isFinite(area) ? area : 0;
  }

  /* THE PRICE, NOT THE RULE.

     Both add-ons are priced from the size, so what goes under the option is the
     number for the size on screen rather than the formula that produced it - the
     treatment is "$50 or $2.50 per square foot, whichever is larger", which is
     true and unhelpful when what you want to know is what it costs.

     Recomputed whenever anything that feeds the area changes. */
  /* THE NUMBER TRAVELS, LIKE THE TOTAL DOES. Apippa's ticker counts the main
     total up to its new value, and next to it the add-on prices snapping felt
     dead. This gives them the same behaviour: on a change the shown value
     counts from the old number to the new over a short ease-out.

     The guard is on data-wk-target, NOT on the text: mid-count the text is an
     intermediate number, and comparing against it would restart the count on
     every applyDesign pass - the mutation-loop trap in a new hat. A stall
     timer clears the running flag in case rAF never comes back (a background
     tab), so the worst case degrades to the old instant swap. Reduced motion
     gets the instant swap by choice. */
  function setMoneyNote(el, prefix, amount) {
    if (!el) return;
    if (amount === null) {
      el.removeAttribute('data-wk-target');
      if (el.textContent !== '') el.textContent = '';
      return;
    }
    var target = Math.round(amount);
    if (el.getAttribute('data-wk-target') === String(target)) return;
    var prev = parseInt((el.textContent || '').replace(/[^0-9]/g, ''), 10);
    el.setAttribute('data-wk-target', String(target));
    var still = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
    var finalText = prefix + '$' + target.toLocaleString('en-US');
    if (!isFinite(prev) || prev === target || still || el.__wkCounting) {
      el.textContent = finalText;
      return;
    }
    el.__wkCounting = true;
    var t0 = performance.now();
    var DUR = 380;
    window.setTimeout(function () { el.__wkCounting = false; }, DUR + 250);
    (function tick(now) {
      /* Re-read the target each frame: a second change mid-count retargets the
         same run instead of fighting it. */
      var goal = parseInt(el.getAttribute('data-wk-target'), 10);
      if (!isFinite(goal)) { el.__wkCounting = false; return; }
      var p = Math.min(1, ((now || performance.now()) - t0) / DUR);
      var eased = 1 - Math.pow(1 - p, 3);
      var v = Math.round(prev + (goal - prev) * eased);
      var text = prefix + '$' + v.toLocaleString('en-US');
      if (el.textContent !== text) el.textContent = text;
      if (p < 1) requestAnimationFrame(tick);
      else el.__wkCounting = false;
    })(t0);
  }

  function optNote(elName, value) {
    var input = document.querySelector(
      '#calculator [data-element="' + elName + '"] .wk-textopt input[value="' + value + '"]');
    var card = input && input.closest('.wk-textopt');
    return card && card.querySelector('.wk-textopt__note');
  }

  function priceAddOns() {
    var area = currentArea();
    setMoneyNote(optNote('Protective Treatment', 'Yes'), '+', area > 0 ? Math.max(50, 2.50 * area) : null);
    /* The calculator's rule: a pad on a ROUND rug carries the round surcharge
       again (docs/CONTRACT.md 1.2), so its note says so. */
    var padExtra = 0;
    var roundOn = document.querySelector('#calculator [data-element="Choose Shape"] input[value="Round"]');
    if (roundOn && roundOn.checked) padExtra = nativeRoundSurcharge();
    setMoneyNote(optNote('Choose Rug Pad', 'Yes'), '+', area > 0 ? 1.80 * area + padExtra : null);

    /* Piggy-backed on this pass because it runs on every input/change/click:
       the clipped select is keyboard-reachable, and nothing else would notice
       a bare select change to clamp it back to the cap. */
    syncQtyBar();
  }

  /* The mirrored total is GONE, deliberately (2026-08-24). It existed because
     Shape and Protective Treatment were design stubs Apippa knew nothing
     about; the master calculator prices both for real, so #priceTicker is the
     truth again and the qty bar shows Apippa's own marker (the CSS defaults
     to it when data-wk-mirror is absent). If a price ever looks wrong now, it
     is wrong in the calculator, not in a mirror. */
  function watchAddOnPrices() {
    priceAddOns();
    /* Registered ONCE. This runs from applyDesign, which re-runs on every
       calculator mutation - without the guard each pass stacked three more
       document-level listeners, and one click was measured firing ~15 duplicate
       handlers within a session. Document-level listeners survive Apippa's
       rebuilds, so once really is enough. */
    if (window.__wkAddOnPriceWatch) return;
    window.__wkAddOnPriceWatch = true;
    ['input', 'change', 'click'].forEach(function (t) {
      document.addEventListener(t, function () { setTimeout(priceAddOns, 0); }, true);
    });
  }

  /* Shape's visibility is Apippa's own conditional now (shown only when
     Choose Size = Custom); what remains ours is the data-wk-size attribute
     everything size-dependent hangs off in the CSS - the unit switch and the
     inches-only fields. Re-queries on every run rather than closing over
     nodes, because Apippa can rebuild the calculator. */
  function syncShapeVisibility() {
    var custom = document.querySelector(
      '#calculator [data-element="Choose Size"] input[value="Custom"]');
    var isCustom = !!(custom && custom.checked);
    var calc = document.getElementById('calculator');
    if (calc && calc.getAttribute('data-wk-size') !== (isCustom ? 'custom' : 'standard')) {
      calc.setAttribute('data-wk-size', isCustom ? 'custom' : 'standard');
    }
  }

  function watchShapeVisibility() {
    syncShapeVisibility();
    /* Registered ONCE - same leak as watchAddOnPrices, same guard. */
    if (window.__wkShapeWatch) return;
    window.__wkShapeWatch = true;
    document.addEventListener('change', function () { setTimeout(syncShapeVisibility, 0); }, true);
    document.addEventListener('click', function () { setTimeout(syncShapeVisibility, 0); }, true);
  }

  /* THE NAME, THEN THE MATERIAL.

     Shopify holds the full name - "Fiji Saddle Indoor/ Outdoor", "GA Mocha
     Sisal" - and that stays exactly as it is. The page shows the rug's name
     large and the material underneath, because the material is a category and
     the name is the thing being looked at.

     Split on the material the collections already say the product is, not by
     guessing at the words. Anything with no material, or on the exclusion list,
     is left alone: "Classic Seagrass" would be reduced to "Classic". */
  function splitTitle() {
    var el = document.querySelector('.product-single__title');
    var material = window.__wkRugMaterial;
    if (!el || !material || window.__wkTitleNoSplit) return;
    if (el.querySelector('.wk-title__material')) return;

    var full = el.textContent.replace(/\s+/g, ' ').trim();
    var flat = full.toLowerCase();
    var tail = material.replace(/\s+/g, ' ').trim().toLowerCase();

    /* Two shapes of title. One that ends in the material ("GA Mocha Sisal")
       splits: the name stays large, the material moves to the line below.
       One that does not ("Seagrass (Test Object...)") cannot be split without
       inventing a name - the title stays whole and the material line is
       simply ADDED, because the category under the name is the point. */
    var name = full;
    if (flat.slice(-tail.length) === tail) {
      var candidate = full.slice(0, full.length - tail.length).replace(/[\s,\-]+$/, '');
      if (candidate) name = candidate;
    }

    el.textContent = name;
    var sub = document.createElement('span');
    sub.className = 'wk-title__material';
    sub.textContent = material + ' Rug';
    el.appendChild(sub);
  }

  /* TWO LINES INSTEAD OF TWO DROPDOWNS.

     The right column ended in a pair of accordions - Shipping information and
     Ask a question - that between them held three sentences and a contact form.
     Both were a click away from saying nothing much, so they become two plain
     lines that say it, and a short note under them about variation.

     The variation wording is a compressed version of the store's own FAQ, not
     new policy: 4% on size described there as an industry standard, colour
     varying between rugs and between screens with natural fiber called out. The
     free-samples line the FAQ also carries was dropped 2026-08-21 on request.

     ⚠️ TWO THINGS THIS DROPS, both deliberate but worth knowing:
       - the 7-day return window, which was the second line of Shipping
         information and is not restated anywhere here
       - the per-product Ask a question FORM, replaced by a link to the contact
         page. That form works and is one of the five live-tested ones.
  */
  /* The assurance rows are rendered by snippets/product-template.liquid at
     the first tab's place (Phase 3b-5, 2026-09-30). */

  /* Reads a number out of a custom property on :root. */
  function rootNum(name, dflt) {
    var v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
    return isFinite(v) ? v : dflt;
  }

  /* The product block's document offset, measured off the grid's PARENT because
     the grid itself is sticky and would report wherever it is currently pinned. */
  function gridTopDoc() {
    var grid = document.querySelector('.grid--product-images--partial');
    var parent = grid && grid.parentElement;
    if (!parent) return 0;
    return parent.getBoundingClientRect().top + (window.scrollY || 0)
         + parseFloat(getComputedStyle(parent).paddingTop || 0);
  }

  /* The gallery crossfades now (section 24 of the page CSS), so the track it
     used to slide never moves. Dragging it would therefore do nothing visible
     while still changing slides, which feels broken - so dragging comes off.
     Thumbnails and the arrows still switch images. */
  /* Clicking the photograph scrolls to state 2 - the point where it has finished
     opening. Only while that would do something: once you are already there the
     click is left alone, so the theme's own image zoom still works.

     Capture phase, because Impulse binds its lightbox to the image itself. */
  function clickToOpenState2() {
    /* No pin, no states, no scroll target on the pad page - a field click
       scrolling the page there was pure disorientation. */
    if (padMode()) return;
    var frame = document.querySelector('.product__main-photos');
    var photos = document.querySelector('.product__photos');

    /* Recomputed only when layout can have changed - it reads computed style
       and layout, and it used to run on EVERY scroll event via mark(), which
       is exactly the per-frame cost the scroll path cannot afford. */
    var targetCache = null;
    function target() {
      if (targetCache !== null) return targetCache;
      var cs = getComputedStyle(document.documentElement);
      var explicit = parseFloat(cs.getPropertyValue('--wk-photo-range'));
      if (isFinite(explicit) && explicit > 0) return (targetCache = Math.round(explicit));
      var hold = rootNum('--wk-photo-hold', NaN);
      if (!isFinite(hold)) return (targetCache = 0);
      var at = rootNum('--wk-photo-hold-at', 1);
      if (!(at > 0 && at <= 1)) at = 1;
      targetCache = Math.round(
        (Math.max(0, gridTopDoc() - rootNum('--wk-photo-pin', 0)) + hold) / at);
      return targetCache;
    }
    addEventListener('resize', function () { targetCache = null; }, { passive: true });
    addEventListener('load', function () { targetCache = null; });

    function armed() { return (window.scrollY || 0) < target() - 2; }

    var lastJump = '';
    function mark() {
      if (!photos) return;
      var state = armed() ? 'on' : 'off';
      if (state === lastJump) return;
      lastJump = state;
      photos.setAttribute('data-wk-jump', state);
    }
    mark();
    addEventListener('scroll', mark, { passive: true });

    function open() {
      var still = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: target(), behavior: still ? 'auto' : 'smooth' });
    }

    if (frame) {
      frame.addEventListener('click', function (ev) {
        if (!armed()) return;                     /* already open: let the theme have it */
        ev.preventDefault();
        ev.stopPropagation();
        open();
      }, true);
    }

    /* Touching ANY field in the calculator opens the page the same way. The click
       is deliberately NOT swallowed here, unlike the photo: it still has to reach
       the input so the field focuses and its zero selects. Bound on the document
       rather than on #calculator, because Apippa rebuilds that element on every
       option change and a listener attached to it would go with it. */
    document.addEventListener('click', function (ev) {
      var t = ev.target;
      if (!t || !t.closest || !t.closest('#calculator')) return;
      /* A length field also asks for the Rug Pad section (watchGuidedScroll).
         Both targets are solved from document geometry, so the further of the
         two is the one scroll - and it never doubles back on the scroll the
         focus path has already started, since that one is the nearer. */
      var more = fieldRevealsRugPad(t) ? rugPadRevealY() : null;
      if (!armed()) { if (more != null) scrollPageTo(more); return; }
      if (more != null && more > target()) scrollPageTo(more); else open();
    }, true);
  }

  /* <details> does not animate its own height - the box just snaps. The open
     and close used to be driven here; since 2026-09-03 that motion is owned by
     assets/wk-unfold.js (every fold on the site travels the same way, and the
     copy unfurls line by line). The rail's fold is handed over explicitly
     because it is built at runtime, after the helper's own sweep. The click
     handler the helper attaches sits on the summary in the bubble phase, which
     is what wirePointsSwap's capture-phase read of d.open relies on. */
  function animateDisclosure(d) {
    var body = d && d.querySelector('.rte');
    if (!body || !window.wkUnfold) return;
    window.wkUnfold.details(d, body);
  }

  /* QTY AND THE TOTAL, AS ONE BAR ON TOP OF ADD TO CART.

     Apippa ships quantity as a <select> of 1..10 and the total as its own
     block, and side by side they never read as a pair. This puts both in a
     white bar sitting directly on the button: how many on the left with real
     step controls, how much on the right.

     The SELECT REMAINS the source of truth and stays in the DOM - it is what
     Apippa reads and what the form submits. It is only taken out of sight,
     and it is clipped rather than display:none so nothing in the app can
     decide it is not there. The buttons drive it and then fire input AND
     change, which is what makes the price recalculate; setting .value alone
     leaves the total stale.

     The bar is built OUTSIDE #calculator, in the form next to the button, so
     Apippa rebuilding the calculator cannot take it with it. The total is a
     different matter: that element IS rebuilt, so it gets re-parented into
     the bar on every applyDesign pass rather than moved once. */
  /* The bar is rendered by snippets/wk-calculator.liquid (Phase 3b-1,
     2026-09-30) with the price marker already in its slot; this only wires the
     steppers once and keeps the value, the end-stops and the cap note in step
     with the select. Re-runnable: applyDesign calls it on every pass. */
  function buildQtyBar() {
    var calc = document.getElementById('calculator');
    var bar = document.getElementById('wk-qtybar');
    if (!calc || !bar) return;
    if (!bar.__wkWired) {
      bar.__wkWired = true;
      bar.addEventListener('click', function (ev) {
        var b = ev.target.closest && ev.target.closest('.wk-qtybar__step');
        if (b) qtyStep(parseInt(b.getAttribute('data-step'), 10));
      });
    }
    syncQtyBar();
  }

  function qtyOptions(select) {
    return [].map.call(select.options, function (o) { return o.value; });
  }

  /* The cap is the pricing contract's 20 per line (the select offers 1 to 20;
     if it is ever shortened, the shorter list wins). Apippa's cap of 5 went
     with Apippa. */
  function maxQty() { return 20; }

  function qtyMaxIndex(opts) {
    var i = opts.indexOf(String(maxQty()));
    return i >= 0 ? i : opts.length - 1;
  }

  function qtyStep(delta) {
    var calc = document.getElementById('calculator');
    var select = calc && calc.querySelector('.element[data-element="Quantity"] select');
    if (!select) return;
    var opts = qtyOptions(select);
    var i = opts.indexOf(select.value);
    if (i < 0) i = 0;
    var next = Math.min(qtyMaxIndex(opts), Math.max(0, i + delta));
    if (next === i) return;
    wkSet(select, opts[next]);
    syncQtyBar();
  }

  function syncQtyBar() {
    var bar = document.getElementById('wk-qtybar');
    var calc = document.getElementById('calculator');
    var select = calc && calc.querySelector('.element[data-element="Quantity"] select');
    if (!bar || !select) return;
    var val = bar.querySelector('.wk-qtybar__value');
    /* Only when changed - an identical write is still a mutation record, and
       this runs on every applyDesign pass. See the note in priceAddOns. */
    if (val && val.textContent !== select.value) val.textContent = select.value;
    var opts = qtyOptions(select);
    var i = opts.indexOf(select.value);
    var maxI = qtyMaxIndex(opts);
    /* The clipped select is still keyboard-reachable; anything past the cap
       is pulled back so the visible UI and the submitted value cannot
       disagree. The dispatch makes the calculator reprice; the re-entrant
       syncQtyBar pass finds the value at the cap and does nothing. */
    if (i > maxI) {
      wkSet(select, opts[maxI]);
      i = maxI;
      if (val) val.textContent = select.value;
    }
    [].forEach.call(bar.querySelectorAll('.wk-qtybar__step'), function (b) {
      var d = parseInt(b.getAttribute('data-step'), 10);
      var atEnd = (d < 0 && i <= 0) || (d > 0 && i >= maxI);
      b.disabled = atEnd;
    });
    /* The note fades in while the cap is the reason + is disabled. */
    bar.classList.toggle('wk-qtybar--max', i >= maxI);
  }

  /* WHAT THE RUG PAD ACTUALLY IS.

     The option is a yes/no with a price and nothing else, which asks someone
     to spend more on a thing they cannot see. This puts the pad's own
     photograph and one sentence behind a marker next to the heading.

     The dialog lives on <body>, NOT inside #calculator, because Apippa
     rebuilds that element on every option change and would take the dialog
     with it - including while it is open. The trigger does live inside, so it
     is rebuilt each pass and re-created here; the dialog is made once and
     found again by id.

     The image is set from CSS rather than here, so no asset URL has to be
     threaded through the JS - a relative url() in the stylesheet resolves
     against assets/ on its own. */
  /* The dialog is rendered by snippets/wk-calculator.liquid (Phase 3b-5,
     2026-09-30); this finds it and wires it once: close on the scrim but not
     on the card, on the close button, and on Escape (bound to the document
     because focus may have moved), and keep Tab inside it while it is open.
     aria-modal promises assistive tech that the rest of the page is inert,
     and without a trap a keyboard user tabbed straight out into the page. */
  function rugPadDialog() {
    var dlg = document.getElementById('wk-rugpad-info');
    if (!dlg || dlg.__wkWired) return dlg;
    dlg.__wkWired = true;
    /* The markup is rendered inside the calculator column, and that column
       carries a transform during the scroll choreography, which would make
       this fixed scrim position itself against the column instead of the
       window (owner saw it off-centre under the header, 2026-09-30). On the
       body it is against the window, as it always was. */
    if (dlg.parentNode !== document.body) document.body.appendChild(dlg);
    dlg.addEventListener('click', function (ev) {
      if (ev.target === dlg || (ev.target.closest && ev.target.closest('.wk-info-close'))) {
        closeRugPadInfo();
      }
    });
    document.addEventListener('keydown', function (ev) {
      if (dlg.hidden) return;
      if (ev.key === 'Escape') { closeRugPadInfo(); return; }
      if (ev.key !== 'Tab') return;
      var focusables = dlg.querySelectorAll('button, [tabindex="-1"]');
      if (!focusables.length) return;
      var first = focusables[0], last = focusables[focusables.length - 1];
      if (ev.shiftKey && document.activeElement === first) {
        ev.preventDefault(); last.focus();
      } else if (!ev.shiftKey && document.activeElement === last) {
        ev.preventDefault(); first.focus();
      }
    });
    return dlg;
  }

  function openRugPadInfo(trigger) {
    var dlg = rugPadDialog();
    if (!dlg) return;
    dlg.__wkReturnTo = trigger || null;
    dlg.hidden = false;
    var card = dlg.querySelector('.wk-info-card');
    if (card) card.focus();
  }

  function closeRugPadInfo() {
    var dlg = document.getElementById('wk-rugpad-info');
    if (!dlg || dlg.hidden || dlg.__wkClosing) return;
    var finish = function () {
      dlg.__wkClosing = false;
      dlg.classList.remove('is-closing');
      dlg.hidden = true;
      /* Send focus back where it came from, or it lands on <body> and the
         next Tab starts from the top of the page. */
      var back = dlg.__wkReturnTo;
      if (back && document.contains(back)) back.focus();
    };
    var still = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (still) { finish(); return; }
    /* The card settles back down and the scrim fades before display:none -
       the reverse of the @starting-style entrance. A timer, not
       transitionend, because the dialog must ALWAYS close. */
    dlg.__wkClosing = true;
    dlg.classList.add('is-closing');
    window.setTimeout(finish, 240);
  }

  function buildRugPadInfo() {
    var el = elementFor('Choose Rug Pad');
    var btn = el && el.querySelector('label .wk-info-trigger');
    if (!btn || btn.__wkWired) return;
    btn.__wkWired = true;
    btn.addEventListener('click', function (ev) {
      ev.preventDefault();
      ev.stopPropagation();          /* the label would otherwise toggle the option */
      openRugPadInfo(btn);
    });
    rugPadDialog();
  }

  /* MOVING BETWEEN STATES WITHOUT THE JUMP.

     The choreography is deliberately simple: OUTGOING fields vanish at once,
     the column's height travels to its new size immediately, and INCOMING
     fields fade in (via @starting-style, section 46 of the page CSS) while it
     moves. One coordinated motion instead of three.

     It used to be more ambitious - allow-discrete held outgoing fields in the
     layout for a 200ms crossfade - and that was the jank being complained
     about: for the length of the fade the column contained BOTH field sets
     stacked inside a frozen height, and the height could not even be measured
     until the crossover finished, so nothing moved for a quarter second and
     then everything did. Instant exits make the layout honest almost at once,
     so the travel starts almost at once.

     THE COLUMN. Custom to Standard removes four fields and a switch and adds a
     size grid, so everything below - the border cards, the pad, Add to cart -
     would snap by a couple of hundred pixels. So the height of #calculator is
     animated across the change: measure before, let the handlers run, measure
     after, then travel between the two.

     Measured on a CLICK in the capture phase, before Apippa's own handler has
     run, which is the only moment the old height still exists.

     setTimeout rather than requestAnimationFrame to wait for the DOM: rAF does
     not run at all in a backgrounded tab, which makes this impossible to verify
     from an automated browser. The transition itself is CSS, so the smoothness
     is the compositor's either way. */
  var CALC_EASE = 'cubic-bezier(.22, .61, .36, 1)';
  var CALC_MS = 320;         /* the resize */
  var MEASURE_DELAY = 60;    /* covers Apippa's own handler and our setTimeout(0) syncs */
  var heightBusy = false;
  var heightTarget = 0;      /* where the running travel is headed */
  var heightRelease = null;  /* the running travel's release, for a retarget */
  var heightReleaseTimer = 0;

  /* EVERY REVEAL, WATCHED AT THE SOURCE. The flash outlived two fixes because
     both were wired to the state-control CLICK - and any reveal that path
     missed (heightBusy re-entry on quick successive clicks, Apippa acting in
     a later task of its own) painted raw. A MutationObserver is the primitive
     that cannot miss: its callback is a microtask at the end of whichever task
     mutated the DOM, before that task's frame renders. It keeps a visibility
     map of the rows; anything that flips to visible is set to opacity 0 in
     that same pre-paint moment and handed its entrance a beat later. No
     dependence on who caused the reveal or what else is running. */
  function watchReveals() {
    if (window.__wkRevealWatch) return;
    window.__wkRevealWatch = true;
    var known = {};
    var lastH = 0;
    function store(s) {
      known = {};
      for (var k in s) known[k] = { v: s[k].visible, top: s[k].top };
    }
    store(snapshotRows(document.getElementById('calculator')));
    /* The height tracker, shared by the mutation observer (a reveal, a toggle) and the resize observer below
       (a silent reflow, the webfonts landing): pin and travel on any change, retarget only on a toggle. */
    function trackHeight(calc, curH, toggleMutation) {
      if (heightBusy) {
        if (toggleMutation) {
          retargetCalcHeight();
          if (heightTarget > 0) lastH = heightTarget;
        }
      } else if (lastH > 0 && Math.abs(curH - lastH) > 2) {
        /* lastH advances to the DESTINATION before the travel starts. Stored
           after instead, it never updated at all - the store was gated on
           !heightBusy, which this very call sets - so the release's own
           mutation found "settled != lastH" and pinned the column back at the
           old short height to travel again, forever: rows at the bottom
           clipped in a blink each cycle, right as the entrance finished.
           That was the disappear-reappear reported on the Length reveal. */
        var fromH = lastH;
        lastH = curH;
        animateCalcHeight(fromH);
      } else {
        lastH = curH;
      }
    }
    /* WATCH THE CALCULATOR, NOT THE DOCUMENT (sweep 2026-10-07). This used to observe the whole document for
       `style` and `class`, which woke it on every scroll frame of the pull-back (img.style.transform, the :root
       writes) and on the scrollbar's writes at rest, each wake re-measuring every row. The calculator is rendered
       with the page since Phase 3b, so it is observed directly; its own attributes still arrive. What the document
       root used to supply by accident, a wake after a silent reflow, the resize observer below supplies on purpose. */
    var calcRoot = document.getElementById('calculator') || document.documentElement;
    new MutationObserver(function (records) {
      var calc = document.getElementById('calculator');
      if (!calc) { known = {}; lastH = 0; return; }
      /* IGNORE OUR OWN HEIGHT WRITES (2026-09-03, owner-reported multi-bounce).
         This observer watches `style` across the subtree to catch Apippa toggling
         `display` on the conditional elements. But animateCalcHeight and
         retargetCalcHeight also write `style` (height/transition/overflow) on the
         calculator itself - and reacting to those re-enters retargetCalcHeight,
         which re-measures the still-settling content and writes the height again,
         which fires this observer again: a self-driven feedback loop that eased
         toward a moving target over and over, the damped 6-bounce on CUSTOM<->
         STANDARD and Binding<->Serging. A batch that is ONLY the calculator's own
         style mutation is self-inflicted - skip it. Real content changes (child
         `display`, childList, data-wk-* toggles) still come through. */
      if (records && records.length && records.every(function (m) {
        return m.type === 'attributes' && m.target === calc && m.attributeName === 'style';
      })) return;
      var still = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
      /* Before the first-paint gate arms, reveals are the page assembling
         itself - track them, never animate them. */
      if (still || !calc.classList.contains('wk-anim-ready')) {
        store(snapshotRows(calc));
        if (!heightBusy) lastH = calc.getBoundingClientRect().height;
        return;
      }

      /* FIELD TOGGLES ARE THE BROWSER'S. The ft/in <-> inches and
         rectangle <-> round changes animate DECLARATIVELY - the fields fold
         through CSS height transitions and everything below rides the layout.
         For that window this watcher only rebases (tops and height tracked
         without animating), or an unrelated mutation mid-fold - a money note
         counting - would read the native motion as moves to counter, and the
         pin would fight a height that is already animating itself. */
      if (calc.__wkFieldsMoving && performance.now() - calc.__wkFieldsMoving < 340) {
        store(snapshotRows(calc));
        if (!heightBusy) lastH = calc.getBoundingClientRect().height;
        /* gt-04b: the fold's own transition (.18s) keeps changing the
           layout AFTER the last mutation of the window, so the snapshot and
           lastH taken here can be mid-fold. The first pass after the window
           used to read that stale height as a change and START A TRAVEL
           from it - the box pinned short, the sticky column re-measured,
           the unit switch jumped and settled. The fold is rebased once more
           when it has finished, and the first full pass after a fold
           rebases instead of animating. */
        calc.__wkFoldSettled = false;
        window.clearTimeout(calc.__wkFoldRebase);
        calc.__wkFoldRebase = window.setTimeout(function () {
          calc.__wkFoldRebase = 0;
          if (calc.__wkFoldSettled) return;
          calc.__wkFoldSettled = true;
          store(snapshotRows(calc));
          if (!heightBusy) lastH = calc.getBoundingClientRect().height;
        }, Math.max(20, 360 - (performance.now() - calc.__wkFieldsMoving)));
        return;
      }
      if (calc.__wkFieldsMoving && calc.__wkFoldSettled === false) {
        /* The window closed before its rebase timer ran: this pass is the
           rebase. Nothing here is an entrance or a travel. */
        window.clearTimeout(calc.__wkFoldRebase);
        calc.__wkFoldRebase = 0;
        calc.__wkFoldSettled = true;
        store(snapshotRows(calc));
        if (!heightBusy) lastH = calc.getBoundingClientRect().height;
        return;
      }

      /* THE CONTAINER, PINNED IN THE SAME BREATH. The height animation used to
         start from the state-control click via a setTimeout - and the browser
         is free to paint between the click task and the timer task, so for one
         frame everything BELOW the calculator sat at the new height before the
         pin snapped it back: the residue of the flash. Height changes are now
         caught here, on the mutation that caused them, and pinned pre-paint;
         animateCalcHeight still measures and travels on its own timer. This
         also ends the need to know which clicks are "state controls" - any
         mutation that moves the height animates, whoever caused it. */
      var curH = calc.getBoundingClientRect().height;
      /* ONLY A GENUINE TOGGLE REDIRECTS A RUNNING TRAVEL (2026-09-03). A running
         height travel must ignore the incidental churn that floods this observer
         during a switch - Apippa rebuilding the size cards, the add-on price
         notes counting up frame by frame, the dim notes updating. Each of those
         used to call retargetCalcHeight, which re-aimed the box at the LIVE
         mid-flight height, so it chased a moving target and oscillated (the
         owner's 6-bounce, measured 325 writes / 35 reversals). Only a real
         data-wk-size/shape/units change - the CUSTOM/STANDARD, shape or unit
         toggle - may bend a travel in flight (the gt-04 re-toggle case). */
      var toggleMutation = !!(records && records.some(function (m) {
        return m.type === 'attributes' && m.target === calc &&
          (m.attributeName === 'data-wk-size' || m.attributeName === 'data-wk-shape' || m.attributeName === 'data-wk-units');
      }));
      trackHeight(calc, curH, toggleMutation);

      var s = snapshotRows(calc);
      var next = {};
      for (var k in s) {
        var cur = s[k], old = known[k];
        /* A row mid-glide or mid-entrance measures at its animated position,
           not its real one - skip it entirely and keep its last honest entry,
           or the next pass would read the correction as a fresh move. */
        if (cur.el.__wkEntering || cur.el.__wkGliding) {
          next[k] = old || { v: cur.visible, top: cur.top };
          continue;
        }
        if (cur.visible && old && old.v === false && cur.el.animate) {
          /* ENTERING: dimmed in this same pre-paint moment, faded in a beat
             later. This frame never shows it. */
          (function (el) {
            el.__wkEntering = true;
            el.style.opacity = '0';
            window.setTimeout(function () {
              el.style.opacity = '';
              el.animate(
                [{ opacity: 0, transform: 'translateY(-6px)' }, { opacity: 1, transform: 'none' }],
                { duration: 260, easing: 'ease' });
              window.setTimeout(function () { el.__wkEntering = false; }, 300);
            }, 50);
            /* Never stranded invisible, whatever happens to the timers. */
            window.setTimeout(function () {
              if (el.style.opacity === '0') el.style.opacity = '';
            }, 700);
          })(cur.el);
        } else if (cur.visible && old && old.v && Math.abs(old.top - cur.top) > 2 && cur.el.animate) {
          /* SURVIVOR THAT MOVED: counter-transformed to its old position in
             this same pre-paint moment, then glided to the new one. This used
             to start at the height-measure timer, 60ms after the layout
             changed - the rows below a reveal snapped to their new places for
             a few frames and then jumped back to glide, which was the flash
             that outlived the entrance fix. */
          (function (el, dy) {
            el.__wkGliding = true;
            el.animate(
              [{ transform: 'translateY(' + dy.toFixed(1) + 'px)' }, { transform: 'none' }],
              { duration: CALC_MS, easing: CALC_EASE });
            window.setTimeout(function () { el.__wkGliding = false; }, CALC_MS + 60);
          })(cur.el, old.top - cur.top);
        }
        next[k] = { v: cur.visible, top: cur.top };
      }
      known = next;
    }).observe(calcRoot, {
      childList: true, subtree: true,
      attributes: true, attributeFilter: ['style', 'class', 'data-wk-shape', 'data-wk-units', 'data-wk-size']
    });
    /* A SILENT REFLOW: a height change with no mutation behind it (the webfonts landing, an image deciding its
       size) is tracked here, so it travels instead of jumping, and lastH never goes stale. The running travel's own
       height writes land here too and are ignored while heightBusy. Deferred a frame so the pin written inside the
       callback cannot trip the observer's loop guard. */
    if (window.ResizeObserver && calcRoot !== document.documentElement) {
      new ResizeObserver(function () {
        window.requestAnimationFrame(function () {
          var calc = document.getElementById('calculator');
          if (!calc) return;
          var still = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
          var curH = calc.getBoundingClientRect().height;
          if (still || !calc.classList.contains('wk-anim-ready')) { if (!heightBusy) lastH = curH; return; }
          trackHeight(calc, curH, false);
        });
      }).observe(calcRoot);
    }
  }

  /* Where every row sits right now, keyed by what it IS rather than by node
     identity, so a row Apippa rebuilds mid-change still matches its old self.
     Tops are relative to #calculator: clickToOpenState2 can be smooth-scrolling
     the page between the two snapshots, and viewport-relative numbers would
     bake that scroll into every glide. */
  function snapshotRows(calc) {
    var rows = {};
    if (!calc) return rows;
    var top = calc.getBoundingClientRect().top;
    var inches = 0;
    [].forEach.call(calc.querySelectorAll('.element, .wk-units, .wk-inches__row'), function (el) {
      var key = el.classList.contains('wk-units') ? 'units'
              : el.classList.contains('wk-inches__row') ? 'inches-' + (inches++)
              : 'el:' + (el.getAttribute('data-element') || '');
      var r = el.getBoundingClientRect();
      rows[key] = { el: el, top: r.top - top, visible: r.height > 2 && r.width > 2 };
    });
    return rows;
  }

  function animateCalcHeight(from) {
    var calc = document.getElementById('calculator');
    if (!calc || !(from > 0)) return;

    var still = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (still) return;

    /* FREEZE FIRST, MEASURE SECOND. Pinning the height at its old value stops
       the content below snapping in the moment before the measurement; with
       exits now instant the layout is honest after one short beat - just long
       enough for Apippa's own handler and our setTimeout(0) syncs to run -
       so the travel starts almost immediately instead of after a 240ms
       crossfade. Measure with height:auto, put the old value back, travel. */
    heightBusy = true;
    calc.style.transition = 'none';
    calc.style.height = from + 'px';
    calc.style.overflow = 'hidden';
    void calc.offsetHeight;

    var release = function () {
      if (!heightBusy) return;
      heightBusy = false;
      heightTarget = 0;
      heightRelease = null;
      window.clearTimeout(heightReleaseTimer);
      calc.style.transition = '';
      calc.style.height = '';
      calc.style.overflow = '';
      calc.removeEventListener('transitionend', onEnd);
    };
    var onEnd = function (ev) {
      if (ev.target === calc && ev.propertyName === 'height') release();
    };
    heightRelease = release;

    window.setTimeout(function () {
      if (!heightBusy) return;

      calc.style.transition = 'none';
      calc.style.height = 'auto';
      var to = calc.getBoundingClientRect().height;

      /* No row work here any more. Entrances AND survivor glides both live in
         watchReveals, which runs pre-paint on the mutation that moved them -
         doing the glide from this timer, 60ms after the layout changed, let
         the rows below a reveal sit at their new positions for a few frames
         and then jump back to glide, which was the flash that outlived the
         entrance fix. This timer only measures and travels the height. */
      calc.style.height = from + 'px';
      void calc.offsetHeight;

      if (Math.abs(to - from) < 2) { release(); return; }

      heightTarget = to;
      calc.addEventListener('transitionend', onEnd);
      calc.style.transition = 'height ' + CALC_MS + 'ms ' + CALC_EASE;
      calc.style.height = to + 'px';

      /* A transition that never fires would strand the column at a fixed
         height, which is far worse than an un-animated change. */
      heightReleaseTimer = window.setTimeout(release, CALC_MS + 220);
    }, MEASURE_DELAY);
  }

  /* The height the calculator's content wants right now, read WITHOUT
     writing a style: the box is pinned and overflow-hidden mid-travel, but
     its children still lay out at their natural places inside it, so the
     lowest visible child's bottom edge (plus the box's own bottom padding
     and border) is the natural height. Read-only matters: this runs from the
     mutation observer, and a style write here would wake the observer again
     - a first version did exactly that and froze the page in a microtask
     loop. */
  function naturalCalcHeight(calc) {
    var top = calc.getBoundingClientRect().top;
    var bottom = top;
    var kids = calc.children;
    for (var i = 0; i < kids.length; i++) {
      var r = kids[i].getBoundingClientRect();
      if (r.height <= 0 && r.width <= 0) continue;
      var mb = parseFloat(getComputedStyle(kids[i]).marginBottom) || 0;
      if (r.bottom + mb > bottom) bottom = r.bottom + mb;
    }
    var cs = getComputedStyle(calc);
    return bottom - top + (parseFloat(cs.paddingBottom) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
  }

  /* Bends a running height travel toward the content's CURRENT natural
     height (gt-04). Changing a transitioning height re-targets the
     transition from wherever the box is this frame - one continuous
     motion, no freeze, no measure dance. Writes ONLY when the target really
     moved, so the observer wakes once more for that write, finds nothing to
     do, and stops. */
  function retargetCalcHeight() {
    var calc = document.getElementById('calculator');
    if (!calc || !heightBusy || !(heightTarget > 0) || !heightRelease) return;
    var to = naturalCalcHeight(calc);
    if (!(to > 0) || Math.abs(to - heightTarget) < 2) return;
    heightTarget = to;
    calc.style.height = to + 'px';
    window.clearTimeout(heightReleaseTimer);
    heightReleaseTimer = window.setTimeout(heightRelease, CALC_MS + 220);
  }

  /* The click-driven height watcher and its isStateControl list are gone:
     watchReveals pins and animates the height on the mutation itself, for any
     cause, pre-paint. */

  /* The fade rules are gated behind this class so nothing animates on FIRST
     paint. @starting-style cannot tell an element being revealed from one
     rendering for the first time, so without the gate the whole calculator
     would fade in on arrival, which reads as a second arrival on top of the
     page's own. */
  function armCalcAnimations() {
    var calc = document.getElementById('calculator');
    if (!calc || calc.classList.contains('wk-anim-ready')) return;
    window.setTimeout(function () {
      var c = document.getElementById('calculator');
      if (c) c.classList.add('wk-anim-ready');
    }, 600);
  }

  /* "MAX WIDTH IS 13 FEET", AND WHY BOTH APPEAR AT ONCE.

     Showing only the one that was breached invites exactly the wrong
     conclusion: someone types 14 into width, is told the maximum is 13, and
     reasonably infers the largest rug they can order is 13 by 13. So either
     limit being exceeded surfaces BOTH, and the pair states the real envelope.

     Read in the CAPTURE phase, because Apippa resets an over-maximum entry to 0
     in its own handler - by the time a bubbling listener runs, the 14 that
     needs warning about is already gone.

     ⚠️ Width and length are not symmetrical underneath. Apippa's Width Feet
     carries max=13 and it enforces that itself; Length Feet carries max=100
     while the page has always claimed 40. So for length this warning is the
     ONLY thing standing between a customer and a 60ft rug that cannot be made.
     Fixing that properly means setting the field maximum to 40 in Apippa's
     admin; it is on the go-live list. Deliberately not clamped here: a theme
     that silently rewrites what someone typed is how you lose an order you
     never hear about. */
  /* THE NOTICE LIVES IN THE GRID, NOT IN THE LABEL (gt-02b, 2026-09-01).
     The warnings used to ride inside the feet label as a flex item, and the
     dimension elements clip their overflow (the fold depends on it), so any
     wording longer than the column - the clamp's "(Max width is 13 feet,
     inches set to 0)" - was cut at the column's edge; on a phone even the
     short "(Max width is 13 feet)" did not fit. One note per axis is now an
     absolutely positioned child of .wk-dim-grid (our own wrapper, no clip),
     laid on the label line of that axis: it starts right after the axis
     word and may run to the grid's right edge, across the inches column's
     empty label line, so it has the whole row. Positioned from the visible
     label each time it is written (ft/in mode: the element's label with its
     painted ::after word; inches mode: the row label), so it follows the
     mode, the round shape's "Diameter" and every width mode. Nothing moves:
     the fields' geometry is untouched. */
  function warnSlot(label) {
    if (!label) return null;
    var calc = document.getElementById('calculator');
    var grid = calc && calc.querySelector('.wk-dim-grid');
    if (!grid) return null;
    var axis = /length/i.test(label.textContent || '') || /length/i.test(getComputedStyle(label, '::after').content || '') ||
               (label.closest('.element') && /^Length/.test(label.closest('.element').getAttribute('data-element') || '')) ||
               (label.classList.contains('wk-inches__label') && label === calc.querySelectorAll('.wk-inches__label')[1]) ? 'l' : 'w';
    var note = grid.querySelector('.wk-dim-note[data-axis="' + axis + '"]');
    if (!note) {
      note = document.createElement('span');
      note.className = 'wk-dim-note';
      note.setAttribute('data-axis', axis);
      note.setAttribute('aria-live', 'polite');
      grid.appendChild(note);
    }
    /* Only a label in an OPEN row may place the note: a folded element or
       row is zero height, but the label inside it still measures 18px (the
       fold clips it), so the host's height is the test, not the label's. */
    var host = label.closest('.element, .wk-inches__row') || label;
    var hr = host.getBoundingClientRect();
    var lr = label.getBoundingClientRect();
    if (hr.height > 2 && lr.width > 2) {
      var gr = grid.getBoundingClientRect();
      var word = 0;
      if (label.classList.contains('wk-inches__label')) {
        var range = document.createRange();
        range.selectNodeContents(label);
        word = range.getBoundingClientRect().width;
      } else {
        word = parseFloat(getComputedStyle(label, '::after').width) || 0;
      }
      note.style.top = Math.round(lr.top - gr.top) + 'px';
      note.style.left = Math.round(lr.left - gr.left + word + 8) + 'px';
    }
    return note;
  }

  function feetOf(name) {
    var el = elementFor(name);
    var i = el && el.querySelector('input.numInput-cpc');
    var v = parseFloat(i && i.value);
    return isFinite(v) ? v : 0;
  }

  /* Whatever the switch says, answer in feet. */
  function enteredSize(rawEl, rawValue) {
    var calc = document.getElementById('calculator');
    var inches = calc && calc.getAttribute('data-wk-units') === 'inches';
    var w, l;

    if (inches) {
      var rows = calc.querySelectorAll('.wk-inches__input');
      var read = function (i) {
        var el = rows[i];
        if (!el) return 0;
        var v = parseFloat(el === rawEl ? rawValue : el.value);
        return isFinite(v) ? v / 12 : 0;
      };
      w = read(0); l = read(1);
    } else {
      w = feetOf('Width Feet') + feetOf('Width Inches') / 12;
      l = feetOf('Length Feet') + feetOf('Length Inches') / 12;
      /* the raw value, before Apippa has had a chance to reset it */
      if (rawEl && rawEl.closest) {
        var owner = rawEl.closest('.element');
        var name = owner && owner.getAttribute('data-element');
        var v = parseFloat(rawValue);
        if (isFinite(v)) {
          if (name === 'Width Feet') w = v + feetOf('Width Inches') / 12;
          if (name === 'Length Feet') l = v + feetOf('Length Inches') / 12;
          if (name === 'Width Inches') w = feetOf('Width Feet') + v / 12;
          if (name === 'Length Inches') l = feetOf('Length Feet') + v / 12;
        }
      }
    }
    return { w: w, l: l };
  }

  function showMaxWarnings(on) {
    var calc = document.getElementById('calculator');
    if (!calc) return;

    var wLab = calc.querySelector('.element[data-element="Width Feet"] > label');
    var lLab = calc.querySelector('.element[data-element="Length Feet"] > label');
    var rows = calc.querySelectorAll('.wk-inches__label');

    var pairs = [
      [wLab, 'Max width is ' + MAX_W_FT + ' feet'],
      [lLab, 'Max length is ' + MAX_L_FT + ' feet'],
      [rows[0], 'Max width is ' + MAX_W_FT + ' feet'],
      [rows[1], 'Max length is ' + MAX_L_FT + ' feet']
    ];

    pairs.forEach(function (p) {
      var span = warnSlot(p[0]);
      if (!span) return;
      /* A clamp notice holds the line for its beat (flashClamp); the
         over-max check must not blank it mid-sentence. */
      var held = span.__wkHoldUntil && Date.now() < span.__wkHoldUntil;
      if (!held) span.textContent = '(' + p[1] + ')';
      span.classList.toggle('is-on', !!on || !!held);
    });
  }

  function watchMaxWarnings() {
    if (window.__wkMaxWarn) return;
    window.__wkMaxWarn = true;
    var check = function (ev) {
      var t = ev.target;
      if (!t || !t.closest || !t.closest('#calculator')) return;
      if (t.tagName !== 'INPUT') return;
      var size = enteredSize(t, t.value);
      showMaxWarnings(size.w > MAX_W_FT + 0.001 || size.l > MAX_L_FT + 0.001);
    };
    document.addEventListener('input', check, true);
    document.addEventListener('change', check, true);
  }

  /* INCHES SNAP TO ZERO AT THE MAXIMUM FEET (owner glitch G-02, 2026-09-01).

     13' 11" and 40' 11" are not sizes the workshop makes: the true maximum is
     13' x 40'. When a feet field reads its maximum and its inches field is
     not zero, the inches are written to 0 through the same input+change
     dispatch every other write here uses, so Apippa reprices the legal size.
     The shopper is told, twice: the axis's max line lights for a beat and
     the inches field flashes. NEVER silent - GO-LIVE item 6's rationale
     stands (a number someone typed is never rewritten quietly), NEVER a
     block, and never display:none. Both axes; both calculator generations
     (the pad page's older "(Feet)" fields resolve through elementFor's
     fallback); STANDARD mode has no typing and is untouched. The inches-only
     mode caps its total at the same ceiling in its own handler and reports
     through the same flash.

     The ceiling is the smaller of Apippa's own max attribute and the page's
     constant: the admin's Length Feet max is still 100 while the page has
     always said 40 (the admin change to 40 is on the GO-LIVE list). */
  var CLAMP_HOLD_MS = 2600;

  function dimInput(name) {
    var el = elementFor(name);
    return el && el.querySelector('input.numInput-cpc');
  }

  function maxFeetFor(name) {
    var page = /^Width/.test(name) ? MAX_W_FT : MAX_L_FT;
    var input = dimInput(name);
    var attr = parseFloat(input && input.getAttribute('max'));
    return isFinite(attr) && attr > 0 ? Math.min(attr, page) : page;
  }

  /* Lights the axis's max line (feet label in ft/in mode, the row label in
     inches mode) with the clamp wording for a beat, and flashes the field. */
  function flashClamp(feetName, input, wording) {
    var calc = document.getElementById('calculator');
    if (!calc) return;
    var isWidth = /^Width/.test(feetName);
    var max = maxFeetFor(feetName);
    var text = wording || ('(Max ' + (isWidth ? 'width' : 'length') + ' is ' + max + ' feet, inches set to 0)');
    var feetEl = elementFor(feetName);
    var rows = calc.querySelectorAll('.wk-inches__label');
    var labels = [feetEl && feetEl.querySelector(':scope > label'), rows[isWidth ? 0 : 1]];
    labels.forEach(function (lab) {
      var span = warnSlot(lab);
      if (!span) return;
      span.textContent = text;
      span.__wkHoldUntil = Date.now() + CLAMP_HOLD_MS;
      span.classList.add('is-on');
      window.clearTimeout(span.__wkHoldTimer);
      span.__wkHoldTimer = window.setTimeout(function () {
        span.__wkHoldUntil = 0;
        /* Re-derive from the fields: still over max keeps the line on. */
        var size = enteredSize(null, null);
        showMaxWarnings(size.w > MAX_W_FT + 0.001 || size.l > MAX_L_FT + 0.001);
      }, CLAMP_HOLD_MS);
    });
    if (input) {
      input.classList.remove('wk-clamp-flash');
      void input.offsetWidth;   /* restart the keyframe on a repeat clamp */
      input.classList.add('wk-clamp-flash');
    }
  }

  function clampInchesAtMax(ev) {
    var t = ev.target;
    if (!t || !t.closest || t.tagName !== 'INPUT' || !t.closest('#calculator')) return;
    var owner = t.closest('.element');
    var name = owner && (owner.getAttribute('data-element') || '');
    if (!/^(Width|Length) (Feet|Inches)$/.test(name)) return;
    var axis = name.split(' ')[0];
    var feet = dimInput(axis + ' Feet'), inches = dimInput(axis + ' Inches');
    if (!feet || !inches) return;
    var f = parseFloat(feet.value), i = parseFloat(inches.value);
    if (!isFinite(f) || !isFinite(i) || i <= 0) return;
    /* Exactly at the maximum. Over it is the standing warning's case. */
    if (Math.abs(f - maxFeetFor(axis + ' Feet')) > 0.001) return;
    wkSet(inches, '0');
    flashClamp(axis + ' Feet', inches);
  }

  function watchInchesClamp() {
    if (window.__wkInchesClamp) return;
    window.__wkInchesClamp = true;
    document.addEventListener('input', clampInchesAtMax, true);
    document.addEventListener('change', clampInchesAtMax, true);
    /* Delegated: Apippa rebuilds the inputs on option changes. */
    document.addEventListener('animationend', function (ev) {
      if (ev.animationName === 'wkClampFlash' && ev.target.classList) ev.target.classList.remove('wk-clamp-flash');
    }, true);
  }

  /* PEG BORDER TO THE TOP WHEN THE BINDING GRID REVEALS.

     Picking Binding drops ~400px of colour grid into the middle of the column,
     and whatever the browser's scroll anchoring decided to hold still, the
     composition lurched. Instead the page commits: it scrolls so the Border
     section sits at the top of the viewport, under the fixed header, with its
     newly revealed colours laid out beneath it.

     The wrinkle is the lag transform: the column hangs back by dy(scrollY), so
     where Border ends up depends on where the scroll ends up. dy is a pure
     function (window.__wkDyAt), so two fixed-point passes solve it - dy is
     monotonic and flat almost everywhere, which makes the iteration exact in
     practice. Only on the reveal, never on re-clicks of an already-open
     Binding, so it cannot fight someone browsing the colours. */
  function scrollBorderToTop() {
    var border = elementFor('Choose Border');
    if (!border) return;
    /* The fixed header's foot, measured (--wk-header is a calc since the site
       notice bar arrived, so it cannot be parsed back as a number). */
    var hdr = document.querySelector('.site-header');
    var header = hdr ? hdr.getBoundingClientRect().bottom : 0;
    var pad = 14;
    var y = window.scrollY || 0;
    var dyNow = window.__wkDyAt ? window.__wkDyAt(y) : 0;
    var baseDoc = border.getBoundingClientRect().top + y - dyNow;
    var target = baseDoc - header - pad;
    if (window.__wkDyAt) {
      target = baseDoc + window.__wkDyAt(target) - header - pad;
      target = baseDoc + window.__wkDyAt(target) - header - pad;
    }
    var still = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(0, Math.round(target)), behavior: still ? 'auto' : 'smooth' });
  }

  function watchBindingReveal() {
    if (window.__wkBindingScroll) return;
    window.__wkBindingScroll = true;
    /* theme.js's binding guard (gt-01) calls this to bring the picker back
       into view when an add is stopped for want of a choice - the column is
       pinned, so its own scrollIntoView lands short. */
    window.__wkRevealBinding = scrollBorderToTop;
    document.addEventListener('change', function (ev) {
      var t = ev.target;
      if (!t || !t.closest) return;
      if (!t.closest('#calculator .element[data-element="Choose Border"]')) return;
      if ((t.value || '') !== 'Binding') return;
      /* Read BEFORE Apippa's own handler reveals the grid: capture phase. */
      var grid = elementFor('Custom Binding');
      var hidden = !grid || getComputedStyle(grid).display === 'none';
      if (!hidden) return;
      /* After the reveal and the FLIP have started, so the measured position
         already includes the grid's height. */
      window.setTimeout(scrollBorderToTop, MEASURE_DELAY + 40);
    }, true);
  }

  /* THE BINDING PICKER, IN TWO STEPS - now over Apippa's real element.

     The master calculator ships Custom Binding as a hidden-by-CSS <select> of
     22 options, SPLIT BY MATERIAL (admin change, adopted 2026-09-01 - owner
     decision d11, never revert it: the workshop needs the material on the
     order line): "Bound to Match Cotton" leads ten Cotton colours, "Bound to
     Match Sunbrella" leads ten Sunbrella colours. THE SELECT IS THE SOURCE OF
     TRUTH - every card and chip below just writes select.value and fires
     change, exactly like the qty bar drives its clipped select, so what
     Apippa submits is always a real option. The two match options are
     matched by PREFIX (MATCH_RE), so a future rename of the suffix cannot
     resurrect the N1 failure (an exact-match card that parked the select at
     selectedIndex -1 and submitted an empty binding).

     Twenty colours at once was a wall; the material is the real first
     decision, so it gets its own pair of option cards - Cotton (Indoor) /
     Sunbrella (Outdoor), design-level state in window.__wkBindMat since
     Apippa has no material element - and only the chosen material's ten
     colours appear, behind ONE selectable BOUND TO MATCH card that selects
     the CURRENT material's own match option. Switching material ALWAYS
     re-targets the select to the new material's match option, colour or no
     colour (owner, 2026-09-02): a colour never outlives its material, so the
     hidden row can never hold the submitted value (rx-03 N9). On a
     fresh load __wkBindMat is READ off the select's default value (its
     material suffix) so the display tells the truth - Cotton checked, the
     card ringed - without writing to the select. The chip images are the
     optimised swatches, shipped as theme assets and rendered into the chips
     by snippets/wk-calculator.liquid (Phase 3b-4).

     window.__wkBindChosen: true once the shopper has CLICKED anything in the
     picker (a material card, the match card, a chip). The select always
     holds a valid value, so "untouched" is a design-state fact, not select
     emptiness; theme.js's add-to-cart guard reads this flag (gt-01). */
  var MATCH_RE = /^bound to match/i;

  function stripMat(name) {
    return (name || '').replace(/\s+(Cotton|Sunbrella)\s*$/i, '');
  }

  /* The material an option value belongs to, read off its suffix. */
  function matOf(value) {
    var m = /\s(Cotton|Sunbrella)\s*$/i.exec(value || '');
    return m ? m[1].toLowerCase() : '';
  }

  /* The split select's match option for a material; Cotton when unset. */
  function matchOptionFor(mat) {
    return 'Bound to Match ' + (mat === 'sunbrella' ? 'Sunbrella' : 'Cotton');
  }

  function hasOption(sel, value) {
    for (var i = 0; i < sel.options.length; i++) {
      if (sel.options[i].value === value) return true;
    }
    return false;
  }

  function bindControl() {
    var el = elementFor('Custom Binding');
    return el && el.querySelector('select.calc-prop');
  }

  function setBinding(value) {
    var sel = bindControl();
    if (!sel || sel.value === value) return;
    /* Never write a value the select does not carry: that parks it at
       selectedIndex -1 and submits an empty binding (the N1 failure). The
       select simply keeps what it has, and syncBindingState shows THAT. */
    if (!hasOption(sel, value)) return;
    wkSet(sel, value);
  }

  /* The picker is rendered by snippets/wk-calculator.liquid (Phase 3b-4,
     2026-09-30); this wires it once. The select is clipped, not hidden, so
     keyboard and screen-reader users can change it directly: a TRUSTED change
     there is a choice too, and the gt-01 guard fails open for it (rx-03 N17).
     isTrusted excludes wkSet's own synthetic dispatches. */
  function buildBindingPicker() {
    var el = elementFor('Custom Binding');
    var sel = el && el.querySelector('select.calc-prop');
    var pick = el && el.querySelector('.wk-bindpick');
    if (!sel || !pick) return;
    if (!sel.__wkChosenWatch) {
      sel.__wkChosenWatch = true;
      sel.addEventListener('change', function (ev) {
        if (ev.isTrusted) window.__wkBindChosen = true;
      });
    }
    if (!pick.__wkWired) {
      pick.__wkWired = true;
      var mat = pick.querySelector('.wk-bindmat');
      if (mat) mat.addEventListener('change', function (ev) {
        window.__wkBindMat = ev.target.value;
        window.__wkBindChosen = true;
        /* A material switch ALWAYS re-targets the select to the new
           material's own Bound to Match option (owner, 2026-09-02; rx-03 N9):
           the display and the select can never disagree. */
        setBinding(matchOptionFor(window.__wkBindMat));
        syncBindingState();
      });
      var matchCard = pick.querySelector('.wk-bindmatch');
      if (matchCard) matchCard.addEventListener('change', function () {
        window.__wkBindChosen = true;
        setBinding(matchOptionFor(window.__wkBindMat));
        syncBindingState();
      });
      pick.addEventListener('click', function (ev) {
        var chip = ev.target.closest && ev.target.closest('.wk-bindchip');
        if (!chip || !pick.contains(chip)) return;
        window.__wkBindChosen = true;
        setBinding(chip.getAttribute('data-value'));
        syncBindingState();
      });
    }
    syncBindingState();
  }

  /* One pass owns every piece of presentation state, all derived from the
     select: the material attribute the CSS gates the groups with, the match
     card's check and ring-quieting attribute, each chip's selected ring, and
     the colour name line. Write-only-when-changed, like everything else that
     runs on Apippa's mutation stream. */
  function syncBindingState() {
    var el = elementFor('Custom Binding');
    var sel = bindControl();
    if (!el || !sel) return;
    var isMatch = MATCH_RE.test(sel.value || '');

    /* The material is READ off the select whenever the select is the
       authority on it: on a fresh load (nothing chosen yet, the select holds
       its default "Bound to Match Cotton") and whenever a match option is
       selected (its suffix IS the material). A material switch re-targets
       the select itself (the card's change handler), so the select and the
       material agree by construction. Read only: nothing here writes
       to the select. */
    var selMat = matOf(sel.value);
    if (selMat && (!window.__wkBindMat || isMatch) && window.__wkBindMat !== selMat) {
      window.__wkBindMat = selMat;
    }

    var wantMat = window.__wkBindMat || '';
    if (el.getAttribute('data-wk-bindmat') !== wantMat) el.setAttribute('data-wk-bindmat', wantMat);
    var wantMatch = isMatch ? '1' : '';
    if (el.getAttribute('data-wk-bindmatch') !== wantMatch) el.setAttribute('data-wk-bindmatch', wantMatch);

    [].forEach.call(el.querySelectorAll('.wk-bindmat input'), function (r) {
      var on = r.value === wantMat;
      if (r.checked !== on) r.checked = on;
    });

    var mi = el.querySelector('.wk-bindmatch input');
    if (mi && mi.checked !== isMatch) mi.checked = isMatch;

    [].forEach.call(el.querySelectorAll('.wk-bindchip'), function (c) {
      var on = !isMatch && c.getAttribute('data-value') === sel.value;
      if (c.classList.contains('is-selected') !== on) c.classList.toggle('is-selected', on);
    });

    var span = el.querySelector('.wk-bindname');
    if (span && !span.__wkHovering) {
      var name = isMatch ? 'Bound to Match' : stripMat(sel.value);
      if (span.textContent !== name) span.textContent = name;
    }
  }

  function watchBindingName() {
    syncBindingState();
    if (window.__wkBindingName) return;
    window.__wkBindingName = true;
    document.addEventListener('change', function () { setTimeout(syncBindingState, 0); }, true);
    /* Hovering a chip previews its name on the same line - this is what
       replaces the old tooltip bubbles - and mouseout hands back to the
       selected colour. */
    document.addEventListener('mouseover', function (ev) {
      var chip = ev.target.closest && ev.target.closest('#calculator .wk-bindchip');
      if (!chip) return;
      var el = elementFor('Custom Binding');
      var span = el && el.querySelector('.wk-bindname');
      if (!span) return;
      span.__wkHovering = true;
      var name = stripMat(chip.getAttribute('data-value'));
      if (span.textContent !== name) span.textContent = name;
    }, true);
    document.addEventListener('mouseout', function (ev) {
      var chip = ev.target.closest && ev.target.closest('#calculator .wk-bindchip');
      if (!chip) return;
      var el = elementFor('Custom Binding');
      var span = el && el.querySelector('.wk-bindname');
      if (span) span.__wkHovering = false;
      setTimeout(syncBindingState, 0);
    }, true);
  }

  /* THE DRAWER'S ADD-A-RUG-PAD, LANDED (2026-08-27). The cart nudge links
     here with the rug line's configuration in the query string:
       ?wk-pad-std=8X10                  - a standard rug: same standard pad
       ?wk-pad-wf=8&wk-pad-wi=6&...     - a custom rug: its raw dimensions
     A custom pad is cut 2" short of the rug on each side (the trade
     convention) - THAT SUBTRACTION LIVES HERE, nowhere else. Everything is
     driven through Apippa's own inputs (real clicks and events), so the
     price is Apippa's own. Retries via the applyDesign watcher until the
     calculator's fields exist, then runs exactly once. */
  function prefillPadFromQuery() {
    if (!padMode() || window.__wkPadPrefilled) return;
    var q = new URLSearchParams(location.search);
    var std = q.get('wk-pad-std');
    var wf = q.get('wk-pad-wf');
    if (!std && wf === null) { window.__wkPadPrefilled = true; return; }

    var sizeEl = elementFor('Choose Size');
    if (!sizeEl) return;   // calculator not up yet - next watcher cycle
    function pickRadio(el, matcher) {
      if (!el) return null;
      var radios = el.querySelectorAll('input[type="radio"]');
      for (var i = 0; i < radios.length; i++) {
        if (matcher(radios[i].value)) return radios[i];
      }
      return null;
    }

    if (std) {
      var norm = std.toUpperCase().replace(/\s|×/g, '').replace(/X/g, 'X');
      var stdRadio = pickRadio(elementFor('Standard Stock Sizes'), function (v) {
        return v.toUpperCase().replace(/\s/g, '') === norm;
      });
      var stdSwitch = pickRadio(sizeEl, function (v) { return v === 'Standard'; });
      if (!stdRadio || !stdSwitch) return;   // sizes not rendered yet
      window.__wkPadPrefilled = true;
      if (!stdSwitch.checked) stdSwitch.click();
      if (!stdRadio.checked) stdRadio.click();
    } else {
      var total = function (f, i) { return (parseFloat(f) || 0) * 12 + (parseFloat(i) || 0); };
      var w = Math.max(0, total(wf, q.get('wk-pad-wi')) - 2);
      var l = Math.max(0, total(q.get('wk-pad-lf'), q.get('wk-pad-li')) - 2);
      if (!w || !l) { window.__wkPadPrefilled = true; return; }
      var field = function (n) {
        var e = elementFor(n);
        return e && e.querySelector('input.numInput-cpc');
      };
      var wfIn = field('Width Feet'), wiIn = field('Width Inches');
      var lfIn = field('Length Feet'), liIn = field('Length Inches');
      if (!wfIn || !wiIn || !lfIn || !liIn) return;
      window.__wkPadPrefilled = true;
      var customSwitch = pickRadio(sizeEl, function (v) { return v === 'Custom'; });
      if (customSwitch && !customSwitch.checked) customSwitch.click();
      wkSet(wfIn, Math.floor(w / 12)); wkSet(wiIn, Math.round(w % 12));
      wkSet(lfIn, Math.floor(l / 12)); wkSet(liIn, Math.round(l % 12));
    }
  }

  function applyDesign() {
    adaptRadios();
    addUnitSwitch();
    addShape();
    prefillPadFromQuery();
    watchShapeVisibility();
    watchAddOnPrices();
    splitTitle();
    buildQtyBar();
    buildRugPadInfo();
    armCalcAnimations();
    watchMaxWarnings();
    watchInchesClamp();
    watchBindingReveal();
    watchBindingName();
    buildBindingPicker();
  }

  /* THE STATE-3 FREEZE, REBUILT ON STICKY. The transform version was the last
     scroll-linked JavaScript motion on the page, and it showed: at the moment
     the freeze engaged, a fast wheel put the compositor a frame ahead of the
     handler - the column dipped and was pulled back, a soft correction where a
     hard lock was wanted. position: sticky is the compositor's own freeze.

     The column's content goes into a sticky wrapper whose containing block
     (.wk-calc-hold) is exactly one --wk-state3-settle taller than the content
     (a ::after spacer in the CSS). With top set to viewport minus content
     height (measure() keeps it current), the content pins the instant its
     last line reaches the fold and holds - immovably, no JavaScript in the
     loop - for exactly the settle's worth of scroll, then travels on. The
     grid item's own tail spacer shrinks by the settle so the photograph's
     travel, and therefore the moment both columns let go, is unchanged. */
  function wrapCalcColumn() {
    var col = document.querySelector('.grid--product-images--partial > .grid__item:last-child');
    if (!col || col.querySelector(':scope > .wk-calc-hold')) return;
    var hold = document.createElement('div');
    hold.className = 'wk-calc-hold';
    var sticky = document.createElement('div');
    sticky.className = 'wk-calc-sticky';
    while (col.firstChild) sticky.appendChild(col.firstChild);
    hold.appendChild(sticky);
    col.appendChild(hold);
  }

  function stopGalleryDrag() {
    var el = document.querySelector('.product-slideshow');
    if (!el || !window.Flickity) return;
    var flkty = window.Flickity.data(el);
    if (!flkty) return;
    flkty.options.draggable = false;
    if (flkty.updateDraggable) flkty.updateDraggable();
  }

  /* KEY POINTS INSTEAD OF THE DESCRIPTION.

     The paragraph that used to sit here was a wall of prose next to a photograph,
     and nobody reads that. These are the same facts as scannable points, taken
     from Whitaker's own product copy rather than invented - every claim below
     appears in the descriptions already on the site.

     Which set shows is decided in Liquid from the product's COLLECTIONS, not
     from its title, and handed over as window.__wkRugProfile. Collections are
     already maintained (Indoor/ Outdoor, Wool, Natural Fiber, plus Jute,
     Seagrass and Sisal), so there is nothing new to keep in sync. Products have
     no type and no tags, so those were not options.

     The full description stays where it was, further down the right-hand column.
     It is not duplicated here. */
  var ICONS = {
    sun: '<circle cx="8" cy="8" r="3.2"/><path d="M8 1v1.6M8 13.4V15M1 8h1.6M13.4 8H15M3.1 3.1l1.1 1.1M11.8 11.8l1.1 1.1M12.9 3.1l-1.1 1.1M4.2 11.8l-1.1 1.1"/>',
    drop: '<path d="M8 1.5s4.2 4.6 4.2 7.3A4.2 4.2 0 0 1 8 13a4.2 4.2 0 0 1-4.2-4.2C3.8 6.1 8 1.5 8 1.5z"/>',
    shield: '<path d="M8 1.5l5 2v4.2c0 3.2-2.1 5.7-5 6.8-2.9-1.1-5-3.6-5-6.8V3.5l5-2z"/>',
    leaf: '<path d="M13.5 2.5C7 2 3 5 3 9.5c0 1.6.6 2.9 1.5 3.9C6 11 8.5 8.5 12 7.5c-2.6 1.6-5 3.9-6.2 6.4"/>',
    home: '<path d="M2.5 7.2L8 2.5l5.5 4.7"/><path d="M4 8.4v5.1h8V8.4"/>',
    weave: '<path d="M2.5 5.5h11M2.5 8h11M2.5 10.5h11M5.5 2.5v11M8 2.5v11M10.5 2.5v11"/>',
    layers: '<path d="M8 2.2l5.4 2.7L8 7.6 2.6 4.9 8 2.2z"/><path d="M2.6 8.1L8 10.8l5.4-2.7"/><path d="M2.6 11.2L8 13.9l5.4-2.7"/>'
  };

  var PROFILES = {
    /* The material leads in every profile (owner, 2026-08-27) - wool and the
       natural fibers already did; outdoor now matches. */
    'indoor-outdoor': [
      ['weave',  '{fiber}',           '{fiberDetail}'],   /* Polypropylene, or P.E.T. for Edisto and Nantucket */
      ['sun',    'Weather resistant', 'Made for sun, rain and damp'],
      ['drop',   'Stain resistant',   'Wipes or hoses clean'],
      ['shield', 'Built for traffic', 'Patios, decks and entryways']
    ],
    'wool': [
      ['weave',  'Natural wool',       'Soft, warm and long lasting'],
      ['shield', 'Naturally resilient','Resists stains and wear'],
      ['home',   'Indoor use only',    'Living rooms, bedrooms, offices'],
      ['drop',   'Easy to clean',      'Dirt vacuums out, spills blot away']
    ],
    'natural-fiber': [
      /* {material} is filled in from the product's own fibre collection, so a
         sisal rug says Sisal rather than listing all three. */
      ['leaf',   'Natural fiber',        '{material}'],
      ['shield', 'Durable',             'Perfect for heavy traffic'],
      ['home',   'Indoor use only',     'Living rooms, bedrooms, offices'],
      ['weave',  'Texture',             'Warm and organic to the touch']
    ]
  };

  /* The two maxima the whole page quotes, written once. The footnote used to
     carry 13 and 40 as literals and the warnings would have carried their own
     pair, which is two places to disagree about the same fact. */
  var MAX_W_FT = 13;
  var MAX_L_FT = 40;
  var FOOTNOTE = 'Any size up to ' + MAX_W_FT + ' ft wide and ' + MAX_L_FT + ' ft long';

  function buildKeyPoints() {
    var points = PROFILES[window.__wkRugProfile];
    if (!points) return null;      /* unknown category: show nothing rather than something wrong */

    /* The fibre collections are the source for {material}. Falling back to the
       generic wording rather than leaving a gap, for a rug that is in Natural
       Fiber but not in one of the three specific collections. */
    function escHtml(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }
    function detail(text) {
      /* Edisto and Nantucket are P.E.T. (owner, 2026-09-14); the Liquid hands the fiber over as __wkRugFiber. */
      if (text === '{fiber}') return window.__wkRugFiber === 'P.E.T.' ? 'P.E.T.' : 'Polypropylene';
      if (text === '{fiberDetail}') return window.__wkRugFiber === 'P.E.T.' ? 'Polyethylene terephthalate' : 'Premium synthetic fiber';
      if (text !== '{material}') return text;
      return window.__wkRugMaterial || 'Seagrass, jute and sisal';
    }

    var wrap = document.createElement('div');
    wrap.className = 'wk-photo-rail__text wk-keypoints';

    var list = document.createElement('ul');
    list.className = 'wk-keypoints__list';
    points.forEach(function (p) {
      var li = document.createElement('li');
      li.innerHTML =
        '<svg class="wk-keypoints__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false" ' +
        'fill="none" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" ' +
        'stroke-linejoin="round">' + ICONS[p[0]] + '</svg>' +
        '<span class="wk-keypoints__label">' + escHtml(detail(p[1])) + '</span>' +
        '<span class="wk-keypoints__detail">' + escHtml(detail(p[2])) + '</span>';
      list.appendChild(li);
    });
    wrap.appendChild(list);

    /* The note simply wraps at the label-tier size. It once carried a fitter
       that stepped its font down until the sentence sat on one line - the
       type-scale consolidation made both of that fitter's inline writes lose
       to !important token rules, so it was measuring and writing for nothing.
       Pruned in the optimization pass. */
    var note = document.createElement('p');
    note.className = 'wk-keypoints__note';
    note.textContent = FOOTNOTE;
    wrap.appendChild(note);
    return wrap;
  }

  /* THE DESCRIPTION TAKES THE POINTS' PLACE. Opening the disclosure slides
     the key points (and the size note) closed while the copy expands into the
     room they leave; closing reverses it. Heights are animated with WAAPI in
     border-box terms so the padding travels with the content, and every path
     carries a timer backstop - onfinish is not delivered in a tab that is not
     rendering, and a missed callback must degrade to the instant swap, never
     to a stranded half-state. */
  function wirePointsSwap(d, container) {
    var summary = d.querySelector('summary');
    if (!summary) return;
    function targets() {
      return [container.querySelector('.wk-keypoints__list'),
              container.querySelector('.wk-keypoints__note')].filter(Boolean);
    }
    /* Capture phase: animateDisclosure toggles d.open in its own handler, so
       reading it here, first, still sees the state being left. */
    summary.addEventListener('click', function () {
      var opening = !d.open;
      var still = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
      /* Mid-swap the outgoing and incoming heights briefly overlap, and the
         scrollable text container grew a scrollbar for exactly that half
         second. Overflow is held shut for the animation and released after
         the same 450ms backstop the animations themselves use. */
      container.style.overflowY = 'hidden';
      window.setTimeout(function () { container.style.overflowY = ''; }, 460);
      targets().forEach(function (el) {
        if (still || !el.animate) {
          el.style.display = opening ? 'none' : '';
          return;
        }
        if (opening) {
          var h = el.getBoundingClientRect().height;
          el.style.overflow = 'hidden';
          el.style.boxSizing = 'border-box';
          var a = el.animate(
            [{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }],
            { duration: 300, easing: 'cubic-bezier(.25, .1, .25, 1)' });
          var hideIt = function () {
            el.style.display = 'none';
            el.style.overflow = '';
            el.style.boxSizing = '';
          };
          a.onfinish = hideIt;
          window.setTimeout(function () { if (d.open && el.style.display !== 'none') hideIt(); }, 450);
        } else {
          el.style.display = '';
          var h2 = el.getBoundingClientRect().height;
          el.style.overflow = 'hidden';
          el.style.boxSizing = 'border-box';
          var settle = function () { el.style.overflow = ''; el.style.boxSizing = ''; };
          var a2 = el.animate(
            [{ height: '0px', opacity: 0 }, { height: h2 + 'px', opacity: 1 }],
            { duration: 300, easing: 'cubic-bezier(.25, .1, .25, 1)' });
          a2.onfinish = settle;
          window.setTimeout(settle, 450);
        }
      });
    }, true);
  }

  /* Builds the rail that fills the space the photo gives up as it opens.

     The description is MOVED here, not copied. Copying it would put the same
     585 characters on the page twice, which search engines and screen readers
     both read as duplication. Moving one node keeps it single.

     Built at runtime rather than in Liquid because the rail is assembled from
     nodes the theme has already rendered elsewhere - the description, thumbs
     and share links are MOVED into it, and placeRail() re-homes them per
     width mode. */
  function buildRail() {
    if (padMode()) return;
    var photos = document.querySelector('.product__photos');
    /* Guard DOCUMENT-wide, not inside photos: placeRail() moves the rail BELOW
       the grid at 1000-1360, and a photos-scoped check missed it there - a
       later rebuild (calculator landing) then built a second rail and every
       point, button and fold rendered twice (found 2026-08-27). */
    if (!photos || document.querySelector('.wk-photo-rail')) return;

    var rail = document.createElement('div');
    rail.className = 'wk-photo-rail';

    /* Key points rather than the description. The description itself is left
       exactly where it was, further down the right-hand column - it is not moved
       and not duplicated. */
    var points = buildKeyPoints();
    if (points) rail.appendChild(points);

    /* The full copy, folded away beneath the made-to-order line. Moved rather
       than copied - the same paragraph twice on one page reads as duplication
       to search engines and to anyone using a screen reader. */
    /* :not(.product__policies) matters: the policies line ("Shipping calculated
       at checkout") is ALSO an .rte in this column, and it only renders once the
       shop's Shipping Policy has a body - so the day the admin policies were
       filled in (2026-08-25), a bare .rte match started grabbing it instead of
       the description. */
    var rte = document.querySelector('.grid--product-images--partial > .grid__item:last-child .rte:not(.product__policies)');
    if (rte && points) {
      var more = document.createElement('details');
      more.className = 'wk-rail-more';
      var sum = document.createElement('summary');
      sum.textContent = 'Full description';
      more.appendChild(sum);
      more.appendChild(rte);
      /* ABOVE the key points, not below: opened, the description REPLACES
         them (wirePointsSwap) - the rail has no room for both, and a reader
         who wants the full copy has outgrown the bullet version anyway. */
      points.insertBefore(more, points.querySelector('.wk-keypoints__list'));
      animateDisclosure(more);
      wirePointsSwap(more, points);
    }

    var thumbs = photos.querySelector('.product__thumbs');
    if (thumbs) rail.appendChild(thumbs);

    /* THE SAMPLE, THEN THE SHARING - the rail's closing moves.

       The sample button links the existing sample-package product. Its label
       carries no price talk: the "$10 flat-rate shipping" note that used to sit
       under it was removed 2026-09-14 (owner) so nobody reads it as the rug's
       shipping. The sample product page states the offer and its shipping.

       PURCHASING TIERS ONLY (owner decision 2026-09-01): the globals snippet
       sets window.__wkSamplesAllowed server-side from render 'is-trade'. When
       it is false - or missing, which fails closed - the CTA is simply not
       built. No apply/login fallback here: gate labels belong to the
       guest-gating pass (audit/scope-guest-gating.md), not this file. */
    if (window.__wkSamplesAllowed === true) {
      var ctaWrap = document.createElement('div');
      ctaWrap.className = 'wk-rail-cta';
      var cta = document.createElement('a');
      cta.className = 'wk-rail-sample';
      cta.href = '/products/rug-sample-package';
      cta.textContent = 'Order samples';   /* one label site-wide (owner, 2026-09-02; rx-03 N23) */
      ctaWrap.appendChild(cta);
      rail.appendChild(ctaWrap);
    }

    /* MOVED, not copied - the same share links twice on one page is the same
       duplication problem as the description. They sat at the bottom of the
       right column in the theme's own dress; the rail restyles them. */
    var share = document.querySelector(
      '.grid--product-images--partial > .grid__item:last-child .social-sharing');
    if (share) {
      share.classList.add('wk-rail-share');
      /* "SHARE  SHARE  PIN IT" - two of the three said the same word. Named
         for where they go instead, read off each link's own destination. */
      [].forEach.call(share.querySelectorAll('.social-sharing__link'), function (a) {
        var span = a.querySelector('.social-sharing__title');
        var h = (a.getAttribute('href') || '').toLowerCase();
        var name = h.indexOf('facebook') > -1 ? 'Facebook'
                 : (h.indexOf('twitter') > -1 || h.indexOf('x.com') > -1) ? 'X'
                 : h.indexOf('pinterest') > -1 ? 'Pinterest' : null;
        if (span && name) span.textContent = name;
      });
      rail.appendChild(share);
    }

    if (!rail.children.length) return;   /* nothing to show, do not add an empty box */
    photos.appendChild(rail);
  }

  /* The photo UN-CROPS as the page scrolls: the frame stays exactly where it is
     and more of the photograph's own height comes into view.

     An earlier version scaled the <img> down while it was object-fit:cover,
     which shrinks the already-cropped result - the same crop, just smaller.

     The move is still one transform; what changed is what it is applied to.
     Under object-fit:CONTAIN a 2:3 portrait source in a landscape frame is
     fitted by HEIGHT, so the rendered picture is exactly as tall as the frame,
     narrower than it, and none of its height is cropped away. Scaling that up
     by k = frameWidth / renderedWidth reproduces object-fit:cover exactly, with
     transform-origin playing the part object-position played. Walking the scale
     from k back down towards 1 therefore walks the crop OPEN: the fraction of
     the source's height on screen is simply 1 / scale - about 54% at rest on
     these shots, all of it at scale 1.

     Every knob stays in the page CSS (the PRODUCT PAGE REDESIGN half of
     custom.css) so the stylesheet remains the single source of truth. Read at
     load and on resize, so a reload picks up an edit to any of them.

     Applied to EVERY gallery image, not just the first. Flickity keeps the
     others in the DOM and a thumbnail click brings one of them to the front. */
  /* THE PER SQUARE FOOT RATE, MOVED TO THE LEFT COLUMN.

     It sat under the product title, at the top of the configurator's own
     column, where it read as the first line of the form rather than as a fact
     about the rug. The left column is where the facts about the rug live, so
     it goes there, above them and larger than them - it is the number every
     other number on the page derives from.

     MOVED, not copied, so there is only ever one of it. Safe to relocate:
     Apippa works off [data-product-price], which is a different element, and
     this label exists only so the rate survives the calculator clearing that
     one. Idempotent because buildRail can run twice. */
  function moveRateToRail() {
    var rail = document.querySelector('.wk-photo-rail__text');
    var rate = document.querySelector('.product__price-unit');
    if (!rail || !rate || rail.contains(rate)) return;
    rate.classList.add('wk-rail-rate');
    rail.insertBefore(rate, rail.firstChild);
  }

  function scrollPullback() {
    if (padMode()) return;
    var imgs = [].slice.call(document.querySelectorAll('.product__main-photos img'));
    var photos = document.querySelector('.product__photos');
    var calcCol = document.querySelector('.grid--product-images--partial > .grid__item:last-child');
    if (!imgs.length) return;
    var narrowMq = window.matchMedia ? matchMedia('(max-width: 1360px)') : null;
    /* TABLET (769-1360): the layout is the desktop pair - photo left,
       calculator right - with the info rail moved BELOW both columns. The
       rail lives inside the photo column's markup, so CSS alone cannot
       reorder it; this walks it out (and home again on a phone width or
       when the desktop composition takes over). */
    function placeRail() {
      var rail = document.querySelector('.wk-photo-rail');
      var grid = document.querySelector('.grid--product-images--partial');
      if (!rail || !grid || !photos) return;
      var thumbs = document.querySelector('.wk-photo-rail .product__thumbs') ||
                   document.querySelector('.product__photos > .product__thumbs');
      var tablet = narrowMq.matches && window.innerWidth >= 1000;
      if (tablet) {
        if (grid.nextElementSibling !== rail) grid.parentNode.insertBefore(rail, grid.nextSibling);
        /* Landscape tablet: the thumbnails leave the rail and stand directly
           under the pinned photograph (owner, 2026-08-27). The band's CSS
           shortens the photo by exactly the strip's height, so the pinned
           block still bottoms out at the fold and the release maths hold. */
        if (thumbs && thumbs.parentNode !== photos) photos.appendChild(thumbs);
      } else {
        if (rail.parentNode !== photos) photos.appendChild(rail);
        /* home again: thumbs ride in the rail, right after the text block */
        if (thumbs && thumbs.parentNode !== rail) {
          var text = rail.querySelector('.wk-photo-rail__text');
          rail.insertBefore(thumbs, text ? text.nextSibling : rail.firstChild);
        }
      }
    }
    function handOverStatic() {
      /* the flow-rail CSS shows everything; these cover the vars it reads */
      /* Impulse computed each image's sizes attribute for the desktop split
         (about 40vw), so stacked full-width boxes were being painted with
         ~530px files - soft and over-cropped. Tell the browser the truth. */
      /* 60vw is right for the 1000-1360 pair (the photo column is ~46-58vw)
         but at 769-999 the frame is ~92-98vw wide, so the browser was picking
         a candidate a third too small - soft on portrait tablets (rx-03 N20). */
      imgs.forEach(function (img) {
        img.sizes = '(min-width: 1000px) 60vw, 100vw';
      });
      placeRail();
      var root = document.documentElement.style;
      root.setProperty('--wk-rail-opacity', '1');
      root.setProperty('--wk-rail-text-opacity', '1');
      root.setProperty('--wk-photo-gap-left', '0px');
      if (photos) photos.setAttribute('data-wk-rail', 'on');
    }
    if (narrowMq && narrowMq.matches) {
      handOverStatic();
      /* phone <-> tablet resizes re-home the rail */
      addEventListener('resize', placeRail, { passive: true });
      if (narrowMq.addEventListener) {
        narrowMq.addEventListener('change', function boot(e) {
          if (!e.matches) {
            narrowMq.removeEventListener('change', boot);
            removeEventListener('resize', placeRail);
            /* the desktop composition positions the rail absolutely INSIDE
               the photo column - put it home before the pull-back boots,
               and the thumbnails back inside the rail where its layout
               expects them */
            var rail = document.querySelector('.wk-photo-rail');
            if (rail && photos && rail.parentNode !== photos) photos.appendChild(rail);
            var thumbs = document.querySelector('.product__photos > .product__thumbs');
            if (rail && thumbs) {
              var text = rail.querySelector('.wk-photo-rail__text');
              rail.insertBefore(thumbs, text ? text.nextSibling : rail.firstChild);
            }
            scrollPullback();
          }
        });
      }
      return;
    }
    if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) {
      /* No move means no progress to fade the rail in with, so it is handed
         over permanently in the tablet arrangement: parked below the grid,
         with everything the CSS reads written once (rx-03 N16). The old
         branch wrote only --wk-rail-opacity, which the CSS no longer honours,
         and left the rail zero-wide and invisible - no rate, no key points,
         no sample button. custom.css shows the flow rail under
         (min-width: 1361px) and (prefers-reduced-motion: reduce). */
      var rmRail = document.querySelector('.wk-photo-rail');
      var rmGrid = document.querySelector('.grid--product-images--partial');
      if (rmRail && rmGrid && rmGrid.nextElementSibling !== rmRail) {
        rmGrid.parentNode.insertBefore(rmRail, rmGrid.nextSibling);
      }
      var rmRoot = document.documentElement.style;
      rmRoot.setProperty('--wk-rail-opacity', '1');
      rmRoot.setProperty('--wk-rail-text-opacity', '1');
      rmRoot.setProperty('--wk-photo-gap-left', '0px');
      if (photos) photos.setAttribute('data-wk-rail', 'on');
      return;
    }

    var shots = [];    /* one entry per image that is actually being cropped */
    var range = 1;     /* scroll distance the move completes over */
    var reveal = 1;    /* fraction of the source's height visible at the end */

    /* EVERYTHING apply() NEEDS, CACHED AT MEASURE TIME. apply() runs on every
       scroll event, and it used to call getComputedStyle on :root a dozen
       times per frame and re-derive the release point (a layout read) each
       time - main-thread cost on the one code path that has to keep up with
       compositor scrolling, which is where the roughness the design was being
       compared on actually came from. None of these change except at a resize
       or breakpoint, and measure() runs on both. */
    var cfgAnchor = 50, cfgFadeFrom = 120, cfgFadeTo = 260;
    var cfgLag = 0, cfgCap = 0, cfgS3 = 0, releaseVal = 0;
    var lastT = -1, lastRail = '';

    /* How much scrolling the opening runs over.

       It is derived from the hold rather than from the viewport, so the two
       cannot drift. The product block is pinned for --wk-photo-hold pixels
       (section 17 of the page CSS), and --wk-photo-hold-at says how far through
       the opening the photo should be at the moment the block lets go. The
       block lets go at (its own document offset + the hold), so the whole move
       has to run over that divided by hold-at. */
    /* The scroll position at which the block stops being pinned. The block pins
       once its top would pass --wk-photo-pin, so the distance travelled before
       the hold even begins is (its document offset - that offset). */
    function releaseAt() {
      var hold = rootNum('--wk-photo-hold', NaN);
      if (!isFinite(hold)) return 0;
      /* The settle is held time AFTER the photo has finished, so it counts
         towards when the block lets go but not towards the opening's pace. */
      return Math.max(0, gridTopDoc() - rootNum('--wk-photo-pin', 0))
           + hold + rootNum('--wk-photo-settle', 0);
    }

    /* How far the configurator column hangs behind the page at scroll y.

       Three segments: the LAG (moves at 1 - lag of page speed until the cap),
       then THE STATE-3 DEAD ZONE - once the last line has cleared the fold the
       column freezes, dy growing 1:1 with the scroll for --wk-state3-settle
       pixels, cancelling it exactly - then constant: the lag has done its job
       and the column travels with the page. The photo's pin ends at the same
       scroll position (see the tail in measure), so everything lets go
       together.

       A pure function of y, and exposed as window.__wkDyAt, because the
       Binding anchor scroll has to solve for the scroll position that puts
       Border at the top of the viewport - and where the column sits at a
       given scroll IS this function. */
    function dyAt(y) {
      var past = Math.max(0, y - releaseVal);
      var freezeStart = (calcCol && calcCol.__wkFreezeStart) || 0;
      if (freezeStart > 0 && cfgS3 > 0 && past >= freezeStart) {
        return Math.min(cfgCap, cfgLag * freezeStart) + Math.min(past - freezeStart, cfgS3);
      }
      return Math.min(cfgCap, cfgLag * past);
    }
    window.__wkDyAt = dyAt;

    function computeRange() {
      var cs = getComputedStyle(document.documentElement);
      /* An explicit override wins, for the cases with no hold to derive from -
         mobile sets one. Unitless px, because a custom property comes back as
         the raw token and resolving a vh value would need a probe element. */
      var explicit = parseFloat(cs.getPropertyValue('--wk-photo-range'));
      if (isFinite(explicit) && explicit > 0) return Math.round(explicit);
      var hold = parseFloat(cs.getPropertyValue('--wk-photo-hold'));
      var at = parseFloat(cs.getPropertyValue('--wk-photo-hold-at'));
      if (!isFinite(hold)) return Math.max(1, Math.round(innerHeight * 0.7));
      if (!(at > 0 && at <= 1)) at = 1;
      /* The block pins once its top would pass --wk-photo-pin, not once it would
         pass the top of the window, so that offset comes off the distance
         travelled before the hold begins. Missing it made the opening finish
         late by exactly the header's height. */
      var pinAt = Math.max(0, gridTopDoc() - rootNum('--wk-photo-pin', 0));
      return Math.max(1, Math.round((pinAt + hold) / at));
    }

    /* object-position is what places the crop at rest, so read the origin back
       out of it rather than hardcoding a percentage here and letting the two
       drift. Returns [x, y] as percentages, defaulting to centre. */
    function anchorOf(img) {
      var parts = (getComputedStyle(img).objectPosition || '').trim().split(/[ ]+/);
      function pct(s) {
        var n = parseFloat(s);
        return (/%$/.test(s || '') && n >= 0 && n <= 100) ? n : 50;
      }
      return [pct(parts[0]), pct(parts[1])];
    }

    function measure() {
      /* Home the rail on EVERY measure, wide or narrow. placeRail is
         idempotent (parentNode comparisons), and the wide branch used to
         skip it, so a 1000-1360 -> >=1361 resize left the rail parked in
         .page-width - absolutely positioned against the wrong ancestor,
         text at opacity 0, pointer-events none - until reload (rx-03 N12). */
      placeRail();
      if (narrowMq && narrowMq.matches) {
        shots = [];
        imgs.forEach(function (img) {
          img.style.removeProperty('transform');
          img.style.removeProperty('object-fit');
        });
        handOverStatic();
        return;
      }
      range = computeRange();
      reveal = rootNum('--wk-photo-reveal', 1);
      if (!(reveal > 0 && reveal <= 1)) reveal = 1;
      cfgAnchor = rootNum('--wk-photo-anchor-x', 50);
      cfgFadeFrom = rootNum('--wk-rail-fade-from', 120);
      cfgFadeTo = rootNum('--wk-rail-fade-to', 260);
      cfgLag = rootNum('--wk-calc-lag', 0);
      cfgCap = rootNum('--wk-calc-lag-max', 0);
      cfgS3 = rootNum('--wk-state3-settle', 0);
      releaseVal = releaseAt();
      /* The geometry just changed, so the next apply() must not skip. */
      lastT = -1; lastRail = '';
      /* Computed HERE, not in apply(). It used to run on every scroll event, and
         it sets a height on the configurator column - which changes the grid's
         height, which changes the sticky geometry the next frame reads, which
         changes the number again. A pixel of rounding either way was enough to
         make that oscillate, and the hold felt like it was stuttering. It depends
         only on layout, so it belongs with the other measurements. */
      /* HOLD THE PHOTOGRAPH UNTIL STATE 3.

         The photograph stays pinned for as long as its column has room to move
         inside the grid, which is (grid height - its own height). The grid is
         only as tall as the configurator, so that came to 147px - and the
         configurator needs 267px of scrolling before its last line clears the
         fold. The photo was therefore leaving 120px before there was anything
         left to read, which is the whole page scrolling while state 3 is still
         arriving.

         Rather than pick a number, the shortfall is worked out and handed to the
         CSS as a tail on the configurator column. A tail makes the grid taller,
         which is what buys the photo its travel, and because it is empty it does
         not move the last line of the configurator - so the photo now runs out
         of travel exactly as that line lands, instead of 120px early.

         Everything in it is measured, so it holds on any window height rather
         than only on the one it was written against. */
      if (calcCol && shots.length) {
        var pin = rootNum('--wk-photo-pin', 0);
        var lagK = rootNum('--wk-calc-lag', 0);
        var lagCap = rootNum('--wk-calc-lag-max', 0);
        var photoCol = document.querySelector('.grid--product-images--partial > .grid__item:first-child');
        var tail = parseFloat(calcCol.style.getPropertyValue('--wk-calc-tail')) || 0;
        var calcH = calcCol.getBoundingClientRect().height - tail;   /* without the tail */
        var photoH = photoCol ? photoCol.getBoundingClientRect().height : 0;

        /* How far the configurator must travel for its last line to clear the
           fold, and how much scrolling that takes given it moves slower than the
           page - at the lag rate until the lag caps, at full rate after. */
        var move = Math.max(0, (pin + calcH) - innerHeight);
        var kneeMove = (lagK > 0 && lagK < 1) ? (lagCap / lagK) * (1 - lagK) : Infinity;
        var need = (move <= kneeMove && lagK < 1) ? move / (1 - lagK) : move + lagCap;

        var have = Math.max(0, calcH - photoH);
        /* Plus a pause once state 3 has arrived, so the composition is not handed
           straight over to the scroll the instant the last line lands - the same
           beat --wk-photo-settle gives state 2. */
        var settle3 = rootNum('--wk-state3-settle', 0);
        calcCol.style.setProperty('--wk-calc-tail',
          Math.max(0, Math.round(need - have + settle3)) + 'px');
        /* Where the state-3 freeze begins, in scroll past the release: the
           point at which the column's last line has just cleared the fold.
           Stored for apply(), which turns the next --wk-state3-settle pixels
           of scroll into a dead zone (see the dy curve there). The photo needs
           no equivalent: the tail above already keeps it pinned through
           exactly need + settle3, so both columns unfreeze together. */
        calcCol.__wkFreezeStart = need;

        /* The sticky wrapper pins when its BOTTOM reaches the fold, which for
           content taller than the viewport means top at viewport minus content
           height. Content shorter than the viewport never needs the freeze -
           top cleared so it can never pin early. */
        var stickyEl = calcCol.querySelector(':scope > .wk-calc-hold > .wk-calc-sticky');
        if (stickyEl) {
          var contentH = stickyEl.getBoundingClientRect().height;
          stickyEl.style.top = contentH > innerHeight
            ? (innerHeight - Math.round(contentH)) + 'px' : '';
        }
      }

      shots = [];
      imgs.forEach(function (img) {
        var W = img.clientWidth, H = img.clientHeight;
        var nw = img.naturalWidth, nh = img.naturalHeight;
        if (!W || !H || !nw || !nh) return;          /* slide not loaded yet */

        /* Size the source renders at under contain, and the factor that turns
           that back into cover. */
        var fit = Math.min(W / nw, H / nh);
        var k = Math.max(W / (nw * fit), H / (nh * fit));
        if (k <= 1.001) return;                      /* nothing is cropped */

        /* Cover crops along whichever axis contain fits EXACTLY; the other axis
           ends up flush and must scale about its centre. Getting this per-axis
           keeps the effect correct for a landscape source or a tall frame, not
           just for these shots. */
        var cropsHorizontally = (W / nw) < (H / nh);
        var a = anchorOf(img);
        /* On the cropping axis the origin has to match object-position or the
           picture drifts. The other axis stays centred, and the shift that holds
           an edge is done with a translate instead - see apply(). */
        var ox = cropsHorizontally ? a[0] : 50;
        var oy = cropsHorizontally ? 50 : a[1];

        /* Inline plain would lose to the sheet's `object-fit: cover !important`,
           so these have to be inline-important to win. */
        img.style.setProperty('object-fit', 'contain', 'important');
        img.style.setProperty('transform-origin', ox + '% ' + oy + '%', 'important');
        img.style.willChange = 'transform';

        shots.push({ img: img, from: k, to: Math.min(k, 1 / reveal),
                     frameW: W, fitW: nw * fit });
      });
    }

    /* THE SCROLL HANDLER HAS TO BE NEARLY FREE. It runs against compositor
       scrolling, and any frame it costs shows up as roughness against pages
       that let the compositor do everything. So: every config value is cached
       (measure), and every write is guarded - once t has clamped at 1 and dy
       has settled, a scroll event does two comparisons and exits. During the
       opening, only the values that changed are written. */
    function apply() {
      if (narrowMq && narrowMq.matches) return;  /* the flow rail owns narrow screens */
      var y = window.scrollY || 0;
      var t = Math.min(1, Math.max(0, y / range));

      if (t !== lastT) {
        lastT = t;
        var root = document.documentElement.style;
        var lead = null, leadScale = 1;

        for (var i = 0; i < shots.length; i++) {
          var s = shots[i];
          var sc = s.from + (s.to - s.from) * t;

          /* WHY A TRANSLATE AND NOT JUST AN ORIGIN.

             Under object-fit:contain the picture is CENTRED inside the img box,
             so the box's right edge is not the picture's right edge - at rest
             they coincide only because the picture happens to fill the box.
             Scaling about the box's right edge therefore does not hold the
             picture's right edge; it drags the picture off to the left, and at
             rest it stopped filling the frame at all.

             Scaling about the centre and then translating is exact. The picture
             is centred, so after scaling its right edge sits
             (frameW - pictureW) / 2 short of the frame's; shifting by that puts
             it back. anchor 50 leaves it centred, 100 holds the right edge, and
             anything between splits the freed space in that ratio. */
          var slack = Math.max(0, s.frameW - s.fitW * sc) / 2;
          var dx = slack * ((cfgAnchor - 50) / 50);
          s.img.style.setProperty(
            'transform', 'translateX(' + dx.toFixed(2) + 'px) scale(' + sc.toFixed(4) + ')', 'important');
          if (!lead) { lead = s; leadScale = sc; }
        }

        root.setProperty('--wk-photo-t', t.toFixed(4));

        /* The picture holds its right edge, so everything it gives up shows up
           on the left as one column. That width is the rail's width, and the
           rail fades on the width rather than on scroll progress - text
           arriving in a 160px slot and reflowing as it grew was worse than
           text arriving late. */
        var gap = lead ? Math.max(0, Math.round(lead.frameW - lead.fitW * leadScale)) : 0;
        root.setProperty('--wk-photo-gap-left', gap + 'px');

        /* The width the rail will finish at. Its contents are laid out to this
           from the start so nothing reflows while the picture narrows - see
           the rail section of the page CSS. */
        if (lead) {
          root.setProperty('--wk-photo-gap-final',
            Math.max(0, Math.round(lead.frameW - lead.fitW * lead.to)) + 'px');
        }

        /* The description waits for the photo to finish and then fades in on
           its own timer. Showing it during the move meant text arriving in a
           column that was still growing, and rewrapping the whole way. */
        root.setProperty('--wk-rail-text-opacity', t >= 0.999 ? '1' : '0');

        var op = (cfgFadeTo > cfgFadeFrom)
          ? Math.min(1, Math.max(0, (gap - cfgFadeFrom) / (cfgFadeTo - cfgFadeFrom)))
          : (gap >= cfgFadeTo ? 1 : 0);
        root.setProperty('--wk-rail-opacity', op.toFixed(3));

        /* An invisible rail must not still be clickable. Keyboard focus is
           unaffected - the CSS brings it back on :focus-within. */
        var railState = op > 0.04 ? 'on' : 'off';
        if (photos && railState !== lastRail) {
          lastRail = railState;
          photos.setAttribute('data-wk-rail', railState);
        }
      }

      /* No transform on the column any more - the state-3 freeze is
         position: sticky (see wrapCalcColumn), and with the lag retired
         nothing else ever displaced it. dyAt stays alive as pure math: the
         Binding anchor still solves against the same displacement curve,
         which sticky now produces in layout instead of JavaScript. */
    }

    /* THE HEIGHT-AWARE RAIL (tb-01 / tb-01b, 2026-09-01). The rail is exactly
       the pinned photograph's height, and its text block (rate, description
       fold, four key points) is the one child allowed to shrink - so on a
       short viewport it was squeezed and clipped its last point mid-line
       (the owner's tablet; the trade tier adds the rate line and the sample
       CTA, which is why guests fit and trade clipped: a height budget).

       OWNER DECISION (tb-01b): on desktop the thumbnails NEVER leave the
       rail - a first cut moved them under the photo like the 1000-1360 band
       does, and that band arrangement stays the tablet's alone. The budget
       is solved inside the rail, in two stages written to
       .product__photos[data-wk-tight] (custom.css, THE TIGHT RAIL):
         compact - tighter rail gaps and padding, a single row of three
                   smaller thumbnails, tighter key-point rows, a shorter
                   rate margin
         scroll  - whatever is still short, the text block scrolls with the
                   same thin scrollbar the open description already uses,
                   instead of hiding a point.
       Neither stage changes the photograph's height, so the frame's
       ResizeObserver never feeds back into this decision; the measure is
       taken fresh from the untouched state on every run (resize/refresh
       only). */
    function fitRail() {
      if (narrowMq && narrowMq.matches) return;
      var rail = document.querySelector('.wk-photo-rail');
      var text = rail && rail.querySelector('.wk-photo-rail__text');
      if (!rail || !text || !photos) return;
      var thumbs = photos.querySelector('.product__thumbs');
      if (thumbs && thumbs.parentNode !== rail) rail.insertBefore(thumbs, text.nextSibling);
      var short = function () { return text.scrollHeight - text.clientHeight; };
      var prev = photos.getAttribute('data-wk-tight') || '';
      photos.removeAttribute('data-wk-tight');
      var next = '';
      if (short() > 2) {
        photos.setAttribute('data-wk-tight', 'compact');
        next = short() > 2 ? 'compact scroll' : 'compact';
      }
      if (next) photos.setAttribute('data-wk-tight', next); else photos.removeAttribute('data-wk-tight');
      return next !== prev;
    }

    function refresh() { measure(); fitRail(); apply(); }

    /* A slide that has not decoded yet has no natural size to measure from. */
    imgs.forEach(function (img) {
      if (!img.complete) img.addEventListener('load', refresh, { once: true });
    });

    refresh();

    /* A ResizeObserver on the frame, not just a window resize listener.

       Without it the whole effect silently did nothing on some loads: measure()
       ran before the gallery had been laid out, so the image reported a 0x0 box
       and was skipped - and because the file was already in cache `complete`
       was true, no load event was ever coming to re-measure on. The frame's box
       is the thing every number here is derived from, so watching that box
       directly is what actually catches it, on the first layout and on any
       later one. Nothing measure() writes affects that box, so it cannot loop.

       The window listeners stay: resize for browsers without the observer, and
       load because late webfonts and Flickity can both move things again. */
    var frame = imgs[0].closest('.product__main-photos');
    if (window.ResizeObserver && frame) new ResizeObserver(refresh).observe(frame);
    /* THE CONFIGURATOR COLUMN TOO. Everything state-3 depends on - the freeze
       start, the tail, the release point - derives from the column's height,
       and at load that height can still change (fonts, the qty bar, reveals).
       Nothing above watches the column, so the numbers stayed load-stale: the
       state-3 dead zone landed wherever the skeleton's height put it, and the
       Binding anchor solved against the same wrong curve - present sometimes,
       absent or misplaced other times, exactly as reported. The column changes
       height when the qty bar builds and on every conditional reveal; re-measuring on each keeps the choreography true.
       Convergent: measure() writes only the tail back to the column, rounded,
       so a re-fire computes the same number and stops. */
    if (window.ResizeObserver && calcCol) new ResizeObserver(refresh).observe(calcCol);
    /* And the sticky content itself: its height is what the pin offset is
       computed FROM, and one late 35px of settling was caught leaving the
       offset stale - the pin then engaged below the fold and read as no
       freeze at all. Watching the element whose number it is closes that. */
    var stickyWatch = document.querySelector('.wk-calc-sticky');
    if (window.ResizeObserver && stickyWatch) new ResizeObserver(refresh).observe(stickyWatch);
    addEventListener('load', refresh);
    /* ResizeObserver callbacks ride the rendering steps, which a throttled
       background tab never runs - and one missed delivery leaves the pin
       offset stale and the freeze landing below the fold. Plain timers fire
       regardless; three late re-measures cost nothing and cover the tail of
       the calculator's settling wherever the tab happens to be. */
    [4000, 8000, 15000].forEach(function (ms) { window.setTimeout(refresh, ms); });
    /* Written straight from the scroll handler: no rAF loop to stall, and
       transform is compositor-friendly. */
    addEventListener('scroll', apply, { passive: true });
    addEventListener('resize', refresh, { passive: true });
  }

  /* ------------------------------------------------------------------------
     THE SCROLL SOLVER (2026-10-07). Every scroll this page makes on purpose -
     the button after Quantity or a rug-pad choice, the Rug Pad section after
     a length, the cards when an add is refused - has to land exactly, and the
     column does not move with the page: the product grid is sticky under the
     header for the hold and the settle (section 17 of the page CSS), and the
     configurator's content freezes inside .wk-calc-hold for the state-3
     settle (section 53). A scrollIntoView measures the box where it sits now
     and lands short by whatever those swallow; the old quantity arithmetic
     modelled the pin from the custom properties, and --wk-photo-pin has been
     a calc() since the header locked (it parses back as 0), with the settle
     never counted - so it, too, landed short from inside the hold.

     The column's position is modelled as a function of the scroll position
     from the live boxes and computed styles instead: the grid rides its
     normal position, pins at its computed `top`, and lets go once its
     container's content box runs out; the sticky content rides the hold,
     pins at its own computed `top` (measure() sets it to the fold minus its
     height) and lets go after the hold's spacer. Both are monotonic in the
     scroll position, so the scroll that puts an element at a given viewport
     offset is found by bisection, to the pixel. On narrow screens nothing is
     sticky and the model is the identity. It is calibrated at the current
     position - the element's measured top is taken as truth there - so any
     residual error is relative to where you are, never absolute. */
  function columnModel() {
    var y = window.scrollY || 0;
    var grid = document.querySelector('.grid--product-images--partial');
    var gridTop = function (Y) { return -Y; };
    var gridNow = 0;
    if (grid && grid.parentElement) {
      var gcs = getComputedStyle(grid);
      var parent = grid.parentElement;
      var pcs = getComputedStyle(parent);
      var pRect = parent.getBoundingClientRect();
      var docTop = pRect.top + y + (parseFloat(pcs.paddingTop) || 0) + (parseFloat(pcs.borderTopWidth) || 0);
      var contentBottom = pRect.bottom + y - (parseFloat(pcs.paddingBottom) || 0) - (parseFloat(pcs.borderBottomWidth) || 0);
      var gRect = grid.getBoundingClientRect();
      var stuck = gcs.position === 'sticky';
      var pin = stuck ? (parseFloat(gcs.top) || 0) : 0;
      var travel = stuck ? Math.max(0, contentBottom - (docTop + gRect.height)) : 0;
      gridTop = function (Y) {
        var n = docTop - Y;
        return stuck ? Math.min(Math.max(n, pin), n + travel) : n;
      };
      gridNow = gRect.top;
    }
    var hold = document.querySelector('.grid--product-images--partial .wk-calc-hold');
    var sticky = hold && hold.querySelector(':scope > .wk-calc-sticky');
    var at = gridTop;
    if (hold && sticky) {
      var scs = getComputedStyle(sticky);
      var T = (scs.position === 'sticky' && scs.top !== 'auto') ? parseFloat(scs.top) : NaN;
      var holdOff = hold.getBoundingClientRect().top - gridNow;
      var sTravel = Math.max(0, hold.getBoundingClientRect().height - sticky.getBoundingClientRect().height);
      at = function (Y) {
        var h = gridTop(Y) + holdOff;
        return isFinite(T) ? Math.min(Math.max(h, T), h + sTravel) : h;
      };
    }
    return { y: y, at: at };
  }

  /* The scroll position that puts el's top at wantTop in the viewport - the
     least such position where a dead zone offers several - clamped to the
     page. */
  function scrollYFor(el, wantTop) {
    var m = columnModel();
    var off = el.getBoundingClientRect().top - m.at(m.y);
    function top(Y) { return m.at(Y) + off; }
    var maxY = Math.max(0, (document.scrollingElement || document.documentElement).scrollHeight - innerHeight);
    var lo = 0, hi = maxY;
    if (top(lo) <= wantTop) return lo;
    if (top(hi) >= wantTop) return hi;
    for (var i = 0; i < 40; i++) {
      var mid = (lo + hi) / 2;
      if (top(mid) > wantTop) lo = mid; else hi = mid;
    }
    return Math.round(hi);
  }
  function scrollPageTo(Y) {
    var still = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(0, Math.round(Y)), behavior: still ? 'auto' : 'smooth' });
  }

  function addToCartButton() {
    var el = document.querySelector(
      '.product-single__form [name="add"], form[action*="/cart/add"] [name="add"], .btn--add-to-cart');
    if (el) return el;
    var all = document.querySelectorAll(
      'form[action*="/cart/add"] button, form[action*="/cart/add"] input[type="submit"]');
    for (var i = 0; i < all.length; i++) {
      if (/add to cart/i.test(all[i].textContent || all[i].value || '')) return all[i];
    }
    return null;
  }

  /* ADD TO CART clear of the fold - which, because the pull-back reads the
     same scroll position, opens the photo on the way down for free. Nothing
     moves when it is already sitting clear. */
  function revealAddToCart() {
    var btn = addToCartButton();
    if (!btn) return;
    var pad = rootNum('--wk-cart-reveal-pad', 40);
    var r = btn.getBoundingClientRect();
    if (r.bottom + pad - innerHeight <= 1) return;
    var Y = scrollYFor(btn, innerHeight - pad - r.height);
    if (Y > (window.scrollY || 0) + 1) scrollPageTo(Y);
  }

  /* THE WAY DOWN (owner, 2026-10-07). At rest the composition ends cleanly at
     the Border cards and nothing says there is more: the Rug Pad, the
     quantity and the button sit just under the fold, and a shopper can take
     the page for finished. So the page leads. A length - the last measurement;
     the diameter of a round rug; a standard size picked - brings the Rug Pad
     section into view, whole, with a little air under it; the rug-pad choice
     then brings ADD TO CART into view, exactly as reaching Quantity always
     has. Each is a no-op when its target is already in view, and the field
     triggers stand down on phones, where the keyboard does the revealing and
     a page that moves under it reads as broken. */
  function rugPadRevealY() {
    var el = elementFor('Choose Rug Pad');
    if (!el || getComputedStyle(el).display === 'none') return null;
    var air = 24;
    var r = el.getBoundingClientRect();
    if (r.bottom + air <= innerHeight) return null;
    var Y = scrollYFor(el, innerHeight - air - r.height);
    return Y > (window.scrollY || 0) + 1 ? Y : null;
  }
  function revealRugPadSection() {
    var Y = rugPadRevealY();
    if (Y != null) scrollPageTo(Y);
  }
  function isLengthField(t) {
    if (!t || !t.closest || !t.closest('#calculator')) return false;
    var calc = document.getElementById('calculator');
    var round = !!calc && calc.getAttribute('data-wk-shape') === 'round';
    var owner = t.closest('.element[data-element]');
    if (owner) {
      var name = owner.getAttribute('data-element') || '';
      if (/^Length (Feet|Inches)$/.test(name)) return true;
      return round && /^Width (Feet|Inches)$/.test(name);
    }
    var row = t.closest('.wk-inches__row');
    if (!row) return false;
    var label = row.querySelector('.wk-inches__label');
    var word = ((label && label.textContent) || '').trim();
    return /^length$/i.test(word) || (round && /^(width|diameter)$/i.test(word));
  }
  function fieldRevealsRugPad(t) {
    return !padMode() && innerWidth > 768 && isLengthField(t);
  }
  /* An add refused for want of the choice (wk-calculator.js): the cards, the
     message under them and the button, together. Hidden under the header the
     section comes to the top; below the fold it comes up whole; in view it
     stays put. */
  function revealRugPadAsk() {
    var el = elementFor('Choose Rug Pad');
    if (!el) return;
    var hdr = document.querySelector('.site-header');
    var header = Math.max(0, hdr ? hdr.getBoundingClientRect().bottom : 0);
    if (el.getBoundingClientRect().top < header + 14) { scrollPageTo(scrollYFor(el, header + 14)); return; }
    revealRugPadSection();
  }
  function watchGuidedScroll() {
    if (window.__wkGuidedScroll) return;
    window.__wkGuidedScroll = true;
    window.__wkRevealRugPad = revealRugPadAsk;
    /* The keyboard path. A click on a length field goes through
       clickToOpenState2's listener instead, which weighs this against the
       state-2 open so the two never double back. */
    document.addEventListener('focusin', function (ev) {
      if (fieldRevealsRugPad(ev.target)) revealRugPadSection();
    });
    document.addEventListener('change', function (ev) {
      var t = ev.target;
      if (!t || !t.closest || !t.closest('#calculator')) return;
      if (t.closest('.element[data-element="Standard Stock Sizes"]')) {
        if (!padMode() && innerWidth > 768) window.setTimeout(revealRugPadSection, 0);
      } else if (t.closest('.element[data-element="Choose Rug Pad"]')) {
        window.setTimeout(revealAddToCart, 0);
      }
    });
  }

  /* Reaching Quantity means the choosing is done, so bring ADD TO CART into
     view. Delegated, because the calculator used to be injected long after
     this ran. focusin covers the keyboard; click covers a pointer landing on
     a select that already holds focus, which fires no focus event of its
     own. The qty bar's steppers count too: quantity interaction moved onto
     them when the bar arrived, and matching only [data-element="Quantity"]
     left this reachable solely through the visually-clipped select. */
  function revealCartOnQuantity() {
    function hit(ev) {
      var t = ev.target;
      if (!t || !t.closest) return null;
      if (t.closest('.wk-qtybar__step')) return t;
      return t.closest('[data-element="Quantity"]') ? t.closest('select, input') : null;
    }
    document.addEventListener('focusin', function (ev) { if (hit(ev)) revealAddToCart(); });
    document.addEventListener('click', function (ev) { if (hit(ev)) revealAddToCart(); });
  }

  /* THE SITE NOTICE BAR (2026-09-16): on this template the bar is fixed above
     the fixed header, and --wk-bar is how far the header moves down. The
     section's own style sets the one-line estimate; this measures the real
     height and keeps it current, since a longer message wraps on phones. */
  function sizeNoticeBar() {
    var bar = document.querySelector('.announcement-bar');
    if (!bar) return;
    var root = document.documentElement;
    function size() { root.style.setProperty('--wk-bar', bar.offsetHeight + 'px'); }
    size();
    window.addEventListener('resize', size);
    if (window.ResizeObserver) new ResizeObserver(size).observe(bar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { sizeNoticeBar(); start(); });
  } else { sizeNoticeBar(); start(); }

})();
